import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  ArrowLeft,
  Bell,
  Boxes,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  Edit3,
  History,
  Loader2,
  LockKeyhole,
  LogOut,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  Truck,
  Trash2,
  X,
} from "lucide-react";

import i18next
  from "i18next";

import {
  reisorProDriverRequest,
  reisorProMarketRequest,
} from "../utils/reisorProMarketHttp.js";

import {
  marketDisplayPlace,
} from "../utils/driverMarketCyrillic.mjs";
import { buildFreightContactDraft } from "../utils/freightContactDraft.js";
import useDraggableFloatingPill from "../hooks/useDraggableFloatingPill.js";

import {
  getReisorProMarketAiQuota,
  getReisorProMarketAiResultSet,
  sendReisorProMarketAiMessage,
} from "../services/reisorAiTransport.js";

import "./ReisorProMarketBoard.css";
import ReisorProThemeToggle from "./ReisorProThemeToggle.jsx";
import DriverAiAssistant from "./DriverAiAssistant.jsx";
import DriverContactActions from "./DriverContactActions.jsx";
import ReisorVehicleMarket from "./ReisorVehicleMarket.jsx";
import ReisorProExpeditorPark from "./ReisorProExpeditorPark.jsx";
import ReisorProExpeditorMyVehicles from "./ReisorProExpeditorMyVehicles.jsx";
import ReisorProExpeditorVehicleModal from "./ReisorProExpeditorVehicleModal.jsx";
import ReisorProExpeditorVehicleStarHub from "./ReisorProExpeditorVehicleStarHub.jsx";
import ReisorProExpeditorInstruction, { expeditorInstructionMeta } from "./ReisorProExpeditorInstruction.jsx";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";
import LanguageSelector from "./LanguageSelector.jsx";
import LegalDocsModal from "./LegalDocsModal.jsx";

const MARKET_PAGE_SIZE = 200;



/* REISOR_PRO_MARKET_SUPPORT_V1 */

const REISOR_PRO_SUPPORT_URL =
  "mailto:support@cargo-platform.online";

function openReisorProSupport() {
  try {
    const opened =
      window.open(
        REISOR_PRO_SUPPORT_URL,
        "_blank",
        "noopener,noreferrer"
      );

    if (!opened) {
      window.location.href =
        REISOR_PRO_SUPPORT_URL;
    }
  } catch {
    try {
      window.location.href =
        REISOR_PRO_SUPPORT_URL;
    } catch {}
  }
}

const COPY = {
  ru: {
    cargoRole:
      "Грузовладелец",
    expeditorRole:
      "Экспедитор",
    aiEntryTitle: "REISOR AI — помощник экспедитора",
    aiEntrySubtitle: "Найдём груз через AI или быстро откроем рабочий раздел.",
    aiEntryCargoTitle: "Что нужно сделать с грузом?",
    aiEntryHot: "Найти груз через REISOR AI",
    aiEntryFindVehicle: "Найти свободную машину",
    aiEntryDeals: "Мои грузы",
    aiEntryPublishCargo: "Разместить груз",
    aiEntryFindCarrier: "Найти перевозчика",
    aiEntryMyCargo: "Мои грузы / отклики",
    aiEntryChat: "Чат с REISOR AI",

    market:
      "REISOR HUB",
    expeditorLoadsTitle:
      "Грузы",
    searchOrders:
      "Поиск заказов",
    availableOrdersPrefix:
      "Доступно",
    availableOrdersSuffix:
      "заказов",
    vehiclesNav:
      "Машины",
    driversNav:
      "Водители",
    targetedDriver:
      "После публикации этот груз будет отправлен водителю",
    targetedInviteSent:
      "Груз опубликован и отправлен выбранному водителю.",
    targetedInviteFailed:
      "Груз опубликован, но адресная отправка не подтверждена. Его можно отправить водителю из раздела «Водители».",
    targetedVehicle:
      "После публикации этот груз будет отправлен выбранной машине",
    targetedVehicleInviteSent:
      "Груз опубликован и отправлен выбранной машине.",
    targetedVehicleInviteFailed:
      "Груз опубликован, но адресная отправка машине не подтверждена. Повторите отправку из раздела «Машины».",
    parkNav:
      "Мой парк",
    starHubTitle:
      "Мои машины",
    settingsNav:
      "Настройки",
    quickCreate:
      "Создать",
    createFreight:
      "Создать груз",
    createVehicle:
      "Создать машину",
    add:
      "Добавить груз",
    mine:
      "Мои грузы",
    logout:
      "Выйти",

    support:
      "Служба поддержки",
    cargoLoadsNav:
      "Грузы",
    updatedAt:
      "Обновлено",
    profile:
      "Профиль",
    accountPhone:
      "Телефон",
    accountRole:
      "Роль",
    accountStatus:
      "Статус",
    accountCreated:
      "Регистрация",
    documentsRules:
      "Документы и правила",
    offer:
      "Оферта",
    paidAccess:
      "Правила доступа",
    privacy:
      "Политика конфиденциальности",
    dataUsage:
      "Использование данных",
    entities:
      "Реквизиты и представительства",
    notifications:
      "Уведомления",
    enableNotifications:
      "Включить уведомления",
    notificationsEnabled:
      "Уведомления подключены",
    notificationsUnavailable:
      "Не удалось подключить уведомления",
    appearance:
      "Оформление",

    back:
      "Назад",

    marketLead:
      "Найдите подходящий груз по маршруту и типу машины.",
    marketLeadCargo:
      "Размещайте свои грузы и контролируйте публикации. Рынок ниже — для обзора.",
    marketLeadExpeditor:
      "Ищите подходящие грузы по маршруту, машине и дате.",
    cargoQuickTitle:
      "Управление грузами",
    cargoQuickText:
      "Добавьте новый груз или откройте свои активные публикации.",

    from:
      "Откуда",
    to:
      "Куда",
    fromPlaceholder:
      "Город или регион отправления",
    toPlaceholder:
      "Город или регион назначения",
    filters:
      "Фильтры",
    filterActiveBadge:
      "Включено",
    savedFilters:
      "Сохранённые",
    findCargo:
      "Найти груз",
    findCargoHint:
      "Маршрут, дата и кузов",
    loads6h:
      "Грузы за 6 ч",
    loads12h:
      "Грузы за 12 ч",
    loads24h:
      "Грузы за 24 ч",
    total24h:
      "24 ч",
    savedSearchTitle:
      "Следить за новыми грузами?",
    saveAndNotify:
      "Сохранить и уведомлять",
    savedSearchSaving:
      "Сохраняем…",
    savedSearchSaved:
      "Поиск сохранён",
    savedSearchError:
      "Не удалось сохранить поиск.",
    savedSearchLimit:
      "Можно сохранить до 20 активных поисков.",
    savedFiltersLoading:
      "Загружаем…",
    savedFiltersEmpty:
      "Сохранённых фильтров пока нет.",
    savedFiltersLoadError:
      "Не удалось загрузить сохранённые фильтры.",
    savedFiltersUpdateError:
      "Не удалось изменить уведомления.",
    savedFiltersDeleteError:
      "Не удалось удалить фильтр.",
    savedFiltersDelete:
      "Удалить",
    savedFiltersApply:
      "Применить",
    savedFiltersNotificationsOn:
      "Уведомления включены",
    savedFiltersNotificationsOff:
      "Уведомления выключены",
    notificationPermissionNeeded:
      "Поиск сохранён. Разрешите уведомления на устройстве, чтобы получать новые грузы.",
    filterCallout:
      "Фильтруйте заказы!",
    filterHint:
      "Найдите подходящий груз быстрее: выберите кузов, Откуда и Куда.",
    filterTruckRequired:
      "Выберите кузов *",
    filterTruckRequiredHint:
      "Кузов обязателен для точного поиска.",
    todayLoading:
      "Загрузка сегодня",
    tomorrowLoading:
      "Загрузка завтра",
    find:
      "Найти",
    showFreights:
      "Показать грузы",
    reset:
      "Сбросить",
    allBodies:
      "Любая машина",
    today:
      "Сегодня",
    tomorrow:
      "Завтра",
    anyDate:
      "Любая дата",
    loadsFound:
      "Найдено грузов",
    refresh:
      "Обновить",
    loadingBusy:
      "Загрузка…",
    showMore:
      "Показать ещё",
    noLoads:
      "По этим условиям грузов пока нет.",
    marketUnavailable:
      "Рынок временно недоступен.",

    cargo:
      "Груз",
    truck:
      "Машина",
    trucks:
      "Машин",
    weight:
      "Вес",
    volume:
      "Объём",
    loadingDate:
      "Дата загрузки",
    ready:
      "Готов к загрузке",
    price:
      "Ставка",
    negotiable:
      "Договорная",
    detail:
      "Подробнее",

    contactsLockedCargo:
      "Контакты скрыты",
    contactsLockedExpeditor:
      "Контакты доступны по подписке",
    contactsLockedTextCargo:
      "Чтобы увидеть контакты, войдите как Экспедитор или Водитель.",
    contactsLockedTextExpeditor:
      "Подключение подписки появится в одном из следующих обновлений.",
    contactsSoonCargo:
      "Вы вошли как Грузовладелец",
    contactsSoonExpeditor:
      "Оплата пока недоступна",

    addTitle:
      "Новый груз",
    editTitle:
      "Редактировать груз",
    step:
      "Шаг",
    routeStep:
      "Маршрут",
    cargoStep:
      "Груз и машина",
    priceStep:
      "Цена и оплата",
    next:
      "Далее",
    previous:
      "Назад",
    publish:
      "Опубликовать бесплатно",
    save:
      "Сохранить",
    publishing:
      "Сохраняем…",
    freePublish:
      "Размещение груза бесплатно.",

    routeRequired:
      "Укажите откуда и куда.",
    sameRoute:
      "Пункт отправления и назначения не должны совпадать.",
    cargoRequired:
      "Укажите груз.",
    weightRequired:
      "Укажите вес.",
    truckRequired:
      "Выберите тип машины.",
    loadingRequired:
      "Укажите дату загрузки или отметьте «Готов к загрузке».",
    priceRequired:
      "Для фиксированной ставки укажите цену.",

    cargoName:
      "Наименование груза",
    cargoPlaceholder:
      "Например: фрукты, оборудование",
    weightTons:
      "Вес, тонн",
    volumeM3:
      "Объём, м³",
    truckType:
      "Тип машины",
    truckCount:
      "Количество машин",
    loading:
      "Загрузка",
    exactDate:
      "По дате",
    readyNow:
      "Груз готов",

    priceMode:
      "Ставка",
    fixed:
      "Фиксированная",
    currency:
      "Валюта",
    advance:
      "Аванс, %",
    payment:
      "Оплата",
    paymentChoose:
      "Выберите способ оплаты",
    paymentRequired:
      "Выберите способ оплаты.",
    cash:
      "Наличными",
    card:
      "Перевод",
    bank:
      "Перечисление",
    comment:
      "Комментарий",
    commentPlaceholder:
      "Условия загрузки, особенности груза…",

    phoneFromAccount:
      "Контакт",
    phoneAuto:
      "Номер берётся из аккаунта автоматически.",

    published:
      "Груз опубликован.",
    updated:
      "Изменения сохранены.",
    requestFailed:
      "Не удалось выполнить запрос.",

    active:
      "Активные",
    history:
      "История",
    noMine:
      "У вас пока нет активных грузов.",
    noHistory:
      "История пока пустая.",
    edit:
      "Изменить",
    close:
      "Завершить",
    closeConfirm:
      "Завершить публикацию этого груза?",
    closeCancel:
      "Отмена",
    closed:
      "Завершён",
    activeStatus:
      "Активен",
  },

  uz: {
    cargoRole:
      "Юк эгаси",
    expeditorRole:
      "Экспедитор",
    aiEntryTitle: "REISOR AI — экспедитор ёрдамчиси",
    aiEntrySubtitle: "AI орқали юк топинг ёки керакли иш бўлимига тез ўтинг.",
    aiEntryCargoTitle: "Юк билан нима қилиш керак?",
    aiEntryHot: "REISOR AI орқали юк топиш",
    aiEntryFindVehicle: "Бўш машина топиш",
    aiEntryDeals: "Менинг юкларим",
    aiEntryPublishCargo: "Юк жойлаштириш",
    aiEntryFindCarrier: "Ташувчи топиш",
    aiEntryMyCargo: "Менинг юкларим / жавоблар",
    aiEntryChat: "REISOR AI билан чат",

    market:
      "REISOR HUB",
    expeditorLoadsTitle:
      "Юклар",
    searchOrders:
      "Буюртмаларни қидириш",
    availableOrdersPrefix:
      "Мавжуд",
    availableOrdersSuffix:
      "та буюртма",
    vehiclesNav:
      "Машиналар",
    driversNav:
      "Ҳайдовчилар",
    targetedDriver:
      "Юк эълон қилингач, танланган ҳайдовчига юборилади",
    targetedInviteSent:
      "Юк эълон қилинди ва танланган ҳайдовчига юборилди.",
    targetedInviteFailed:
      "Юк эълон қилинди, лекин манзилли юбориш тасдиқланмади. Уни «Ҳайдовчилар» бўлимидан юбориш мумкин.",
    parkNav:
      "Менинг парким",
    starHubTitle:
      "Менинг машиналарим",
    settingsNav:
      "Созламалар",
    quickCreate:
      "Яратиш",
    createFreight:
      "Юк яратиш",
    createVehicle:
      "Машина яратиш",
    add:
      "Юк қўшиш",
    mine:
      "Менинг юкларим",
    logout:
      "Чиқиш",

    support:
      "Қўллаб-қувватлаш",
    cargoLoadsNav:
      "Юклар",
    updatedAt:
      "Янгиланди",
    profile:
      "Профиль",
    accountPhone:
      "Телефон",
    accountRole:
      "Роль",
    accountStatus:
      "Ҳолат",
    accountCreated:
      "Рўйхатдан ўтган",
    documentsRules:
      "Ҳужжатлар ва қоидалар",
    offer:
      "Оферта",
    paidAccess:
      "Кириш қоидалари",
    privacy:
      "Махфийлик сиёсати",
    dataUsage:
      "Маълумотлардан фойдаланиш",
    entities:
      "Реквизитлар ва ваколатхоналар",
    notifications:
      "Билдиришномалар",
    enableNotifications:
      "Билдиришномаларни ёқиш",
    notificationsEnabled:
      "Билдиришномалар уланди",
    notificationsUnavailable:
      "Билдиришномаларни улаб бўлмади",
    appearance:
      "Кўриниш",

    back:
      "Орқага",

    marketLead:
      "Йўналиш ва машина тури бўйича мос юкни топинг.",
    marketLeadCargo:
      "Юкларингизни жойланг ва эълонларни бошқаринг. Қуйидаги бозор — умумий кўриб чиқиш учун.",
    marketLeadExpeditor:
      "Йўналиш, машина ва сана бўйича мос юкларни топинг.",
    cargoQuickTitle:
      "Юкларни бошқариш",
    cargoQuickText:
      "Янги юк қўшинг ёки фаол эълонларингизни очинг.",

    from:
      "Қаердан",
    to:
      "Қаерга",
    fromPlaceholder:
      "Юклаш шаҳри ёки ҳудуди",
    toPlaceholder:
      "Тушириш шаҳри ёки ҳудуди",
    filters:
      "Фильтрлар",
    filterActiveBadge:
      "Ёқилган",
    savedFilters:
      "Сақланган",
    findCargo:
      "Юк топиш",
    findCargoHint:
      "Йўналиш, сана ва кузов",
    loads6h:
      "6 соатлик юклар",
    loads12h:
      "12 соатлик юклар",
    loads24h:
      "24 соатлик юклар",
    total24h:
      "24 соат",
    savedSearchTitle:
      "Янги юкларни кузатасизми?",
    saveAndNotify:
      "Сақлаш ва хабар бериш",
    savedSearchSaving:
      "Сақланмоқда…",
    savedSearchSaved:
      "Қидирув сақланди",
    savedSearchError:
      "Қидирувни сақлаб бўлмади.",
    savedSearchLimit:
      "20 тагача фаол қидирув сақлаш мумкин.",
    savedFiltersLoading:
      "Юкланмоқда…",
    savedFiltersEmpty:
      "Ҳозирча сақланган фильтрлар йўқ.",
    savedFiltersLoadError:
      "Сақланган фильтрларни юклаб бўлмади.",
    savedFiltersUpdateError:
      "Хабар бериш ҳолатини ўзгартириб бўлмади.",
    savedFiltersDeleteError:
      "Фильтрни ўчириб бўлмади.",
    savedFiltersDelete:
      "Ўчириш",
    savedFiltersApply:
      "Қўллаш",
    savedFiltersNotificationsOn:
      "Хабарлар ёқилган",
    savedFiltersNotificationsOff:
      "Хабарлар ўчирилган",
    notificationPermissionNeeded:
      "Қидирув сақланди. Янги юклар ҳақида хабар олиш учун қурилмада хабарларга рухсат беринг.",
    filterCallout:
      "Буюртмаларни фильтрланг!",
    filterHint:
      "Мос юкни тезроқ топинг: кузов, Қаердан ва Қаерга ни танланг.",
    filterTruckRequired:
      "Кузовни танланг *",
    filterTruckRequiredHint:
      "Аниқ қидирув учун кузовни танлаш керак.",
    todayLoading:
      "Бугун юкланади",
    tomorrowLoading:
      "Эртага юкланади",
    find:
      "Топиш",
    showFreights:
      "Юкларни кўрсатиш",
    reset:
      "Тозалаш",
    allBodies:
      "Ҳар қандай машина",
    today:
      "Бугун",
    tomorrow:
      "Эртага",
    anyDate:
      "Ҳар қандай сана",
    loadsFound:
      "Топилган юклар",
    refresh:
      "Янгилаш",
    loadingBusy:
      "Юкланмоқда…",
    showMore:
      "Яна кўрсатиш",
    noLoads:
      "Бу шартлар бўйича ҳозирча юк йўқ.",
    marketUnavailable:
      "Юклар бозори вақтинча ишламаяпти.",

    cargo:
      "Юк",
    truck:
      "Машина",
    trucks:
      "Машиналар",
    weight:
      "Оғирлик",
    volume:
      "Ҳажм",
    loadingDate:
      "Юклаш санаси",
    ready:
      "Юклашга тайёр",
    price:
      "Нарх",
    negotiable:
      "Келишилади",
    detail:
      "Батафсил",

    contactsLockedCargo:
      "Контактлар ёпиқ",
    contactsLockedExpeditor:
      "Контактлар обуна орқали очилади",
    contactsLockedTextCargo:
      "Контактларни кўриш учун Экспедитор ёки Ҳайдовчи сифатида киринг.",
    contactsLockedTextExpeditor:
      "Обунага уланиш кейинги янгиланишлардан бирида пайдо бўлади.",
    contactsSoonCargo:
      "Сиз Юк эгаси сифатида киргансиз",
    contactsSoonExpeditor:
      "Тўлов ҳозирча мавжуд эмас",

    addTitle:
      "Янги юк",
    editTitle:
      "Юкни таҳрирлаш",
    step:
      "Қадам",
    routeStep:
      "Йўналиш",
    cargoStep:
      "Юк ва машина",
    priceStep:
      "Нарх ва тўлов",
    next:
      "Давом этиш",
    previous:
      "Орқага",
    publish:
      "Бепул жойлаштириш",
    save:
      "Сақлаш",
    publishing:
      "Сақланмоқда…",
    freePublish:
      "Юк жойлаштириш бепул.",

    routeRequired:
      "Қаердан ва қаерга эканини киритинг.",
    sameRoute:
      "Юклаш ва тушириш жойи бир хил бўлмаслиги керак.",
    cargoRequired:
      "Юкни киритинг.",
    weightRequired:
      "Оғирликни киритинг.",
    truckRequired:
      "Машина турини танланг.",
    loadingRequired:
      "Юклаш санасини киритинг ёки «Юк тайёр»ни танланг.",
    priceRequired:
      "Белгиланган нарх учун суммани киритинг.",

    cargoName:
      "Юк номи",
    cargoPlaceholder:
      "Масалан: мева, ускуна",
    weightTons:
      "Оғирлик, тонна",
    volumeM3:
      "Ҳажм, м³",
    truckType:
      "Машина тури",
    truckCount:
      "Машина сони",
    loading:
      "Юклаш",
    exactDate:
      "Сана бўйича",
    readyNow:
      "Юк тайёр",

    priceMode:
      "Нарх",
    fixed:
      "Белгиланган",
    currency:
      "Валюта",
    advance:
      "Аванс, %",
    payment:
      "Тўлов",
    paymentChoose:
      "Тўлов усулини танланг",
    paymentRequired:
      "Тўлов усулини танланг.",
    cash:
      "Нақд",
    card:
      "Картага ўтказма",
    bank:
      "Ҳисоб рақамига ўтказма",
    comment:
      "Изоҳ",
    commentPlaceholder:
      "Юклаш шартлари, юк хусусиятлари…",

    phoneFromAccount:
      "Контакт",
    phoneAuto:
      "Телефон рақами аккаунтдан автоматик олинади.",

    published:
      "Юк жойлаштирилди.",
    updated:
      "Ўзгаришлар сақланди.",
    requestFailed:
      "Сўровни бажариб бўлмади.",

    active:
      "Фаол",
    history:
      "Тарих",
    noMine:
      "Ҳозирча фаол юкларингиз йўқ.",
    noHistory:
      "Тарих ҳозирча бўш.",
    edit:
      "Ўзгартириш",
    close:
      "Якунлаш",
    closeConfirm:
      "Бу юк эълонини якунлайсизми?",
    closeCancel:
      "Бекор қилиш",
    closed:
      "Якунланган",
    activeStatus:
      "Фаол",
  },

  tr: {
    cargoRole:
      "Yük sahibi",
    expeditorRole:
      "Nakliye organizatörü",
    aiEntryTitle: "REISOR AI — ekspeditör yardımcısı",
    aiEntrySubtitle: "AI ile yük bulun veya çalışma bölümüne hızlı geçin.",
    aiEntryCargoTitle: "Yük için ne yapmak istiyorsunuz?",
    aiEntryHot: "REISOR AI ile yük bul",
    aiEntryFindVehicle: "Müsait araç bul",
    aiEntryDeals: "Yüklerim",
    aiEntryPublishCargo: "Yük yayınla",
    aiEntryFindCarrier: "Taşıyıcı bul",
    aiEntryMyCargo: "Yüklerim / yanıtlar",
    aiEntryChat: "REISOR AI ile sohbet",

    market:
      "REISOR HUB",
    expeditorLoadsTitle:
      "Yükler",
    searchOrders:
      "Sipariş ara",
    availableOrdersPrefix:
      "Mevcut",
    availableOrdersSuffix:
      "sipariş",
    vehiclesNav:
      "Araçlar",
    driversNav:
      "Sürücüler",
    targetedDriver:
      "Yayınlandıktan sonra bu yük seçilen sürücüye gönderilecek",
    targetedInviteSent:
      "Yük yayınlandı ve seçilen sürücüye gönderildi.",
    targetedInviteFailed:
      "Yük yayınlandı ancak hedefli gönderim doğrulanamadı. Sürücüler bölümünden tekrar gönderebilirsiniz.",
    parkNav:
      "Filom",
    starHubTitle:
      "Araçlarım",
    settingsNav:
      "Ayarlar",
    quickCreate:
      "Oluştur",
    createFreight:
      "Yük oluştur",
    createVehicle:
      "Araç oluştur",
    add:
      "Yük ekle",
    mine:
      "Yüklerim",
    logout:
      "Çıkış",

    support:
      "Destek",
    cargoLoadsNav:
      "Yükler",
    updatedAt:
      "Güncellendi",
    profile:
      "Profil",
    accountPhone:
      "Telefon",
    accountRole:
      "Rol",
    accountStatus:
      "Durum",
    accountCreated:
      "Kayıt",
    documentsRules:
      "Belgeler ve kurallar",
    offer:
      "Teklif / sözleşme",
    paidAccess:
      "Erişim kuralları",
    privacy:
      "Gizlilik politikası",
    dataUsage:
      "Veri kullanımı",
    entities:
      "Bilgiler ve temsilcilikler",
    notifications:
      "Bildirimler",
    enableNotifications:
      "Bildirimleri etkinleştir",
    notificationsEnabled:
      "Bildirimler etkin",
    notificationsUnavailable:
      "Bildirimler etkinleştirilemedi",
    appearance:
      "Görünüm",

    back:
      "Geri",

    marketLead:
      "Rota ve araç tipine göre uygun yükleri bulun.",
    marketLeadCargo:
      "Yüklerinizi yayınlayın ve ilanlarınızı yönetin. Aşağıdaki pazar genel görünüm içindir.",
    marketLeadExpeditor:
      "Rota, araç ve tarihe göre uygun yükleri bulun.",
    cargoQuickTitle:
      "Yük yönetimi",
    cargoQuickText:
      "Yeni yük ekleyin veya aktif ilanlarınızı açın.",

    from:
      "Nereden",
    to:
      "Nereye",
    fromPlaceholder:
      "Yükleme şehri veya bölgesi",
    toPlaceholder:
      "Teslimat şehri veya bölgesi",
    filters:
      "Filtreler",
    filterActiveBadge:
      "Açık",
    savedFilters:
      "Kaydedilenler",
    findCargo:
      "Yük bul",
    findCargoHint:
      "Rota, tarih ve araç tipi",
    loads6h:
      "6 saatlik yükler",
    loads12h:
      "12 saatlik yükler",
    loads24h:
      "24 saatlik yükler",
    total24h:
      "24 saat",
    savedSearchTitle:
      "Yeni yükleri takip et?",
    saveAndNotify:
      "Kaydet ve bildir",
    savedSearchSaving:
      "Kaydediliyor…",
    savedSearchSaved:
      "Arama kaydedildi",
    savedSearchError:
      "Arama kaydedilemedi.",
    savedSearchLimit:
      "En fazla 20 aktif arama kaydedebilirsiniz.",
    savedFiltersLoading:
      "Yükleniyor…",
    savedFiltersEmpty:
      "Henüz kaydedilmiş filtre yok.",
    savedFiltersLoadError:
      "Kaydedilmiş filtreler yüklenemedi.",
    savedFiltersUpdateError:
      "Bildirim ayarı değiştirilemedi.",
    savedFiltersDeleteError:
      "Filtre silinemedi.",
    savedFiltersDelete:
      "Sil",
    savedFiltersApply:
      "Uygula",
    savedFiltersNotificationsOn:
      "Bildirimler açık",
    savedFiltersNotificationsOff:
      "Bildirimler kapalı",
    notificationPermissionNeeded:
      "Arama kaydedildi. Yeni yük bildirimleri için cihaz bildirimlerine izin verin.",
    filterCallout:
      "Yükleri filtreleyin!",
    filterHint:
      "Uygun yükü daha hızlı bulun: araç tipi, Nereden ve Nereye seçin.",
    filterTruckRequired:
      "Araç tipi seçin *",
    filterTruckRequiredHint:
      "Doğru arama için araç tipi seçilmelidir.",
    todayLoading:
      "Bugün yükleme",
    tomorrowLoading:
      "Yarın yükleme",
    find:
      "Ara",
    showFreights:
      "Yükleri göster",
    reset:
      "Sıfırla",
    allBodies:
      "Tüm araçlar",
    today:
      "Bugün",
    tomorrow:
      "Yarın",
    anyDate:
      "Tüm tarihler",
    loadsFound:
      "Bulunan yükler",
    refresh:
      "Yenile",
    loadingBusy:
      "Yükleniyor…",
    showMore:
      "Daha fazla göster",
    noLoads:
      "Bu koşullarda henüz yük yok.",
    marketUnavailable:
      "REISOR HUB geçici olarak kullanılamıyor.",

    cargo:
      "Yük",
    truck:
      "Araç",
    trucks:
      "Araç sayısı",
    weight:
      "Ağırlık",
    volume:
      "Hacim",
    loadingDate:
      "Yükleme tarihi",
    ready:
      "Yüklemeye hazır",
    price:
      "Fiyat",
    negotiable:
      "Pazarlık",
    detail:
      "Detay",

    contactsLockedCargo:
      "İletişim bilgileri gizli",
    contactsLockedExpeditor:
      "İletişim bilgileri abonelikle açılır",
    contactsLockedTextCargo:
      "İletişim bilgilerini görmek için Nakliye organizatörü veya Sürücü olarak giriş yapın.",
    contactsLockedTextExpeditor:
      "Abonelik bağlantısı sonraki güncellemelerden birinde açılacaktır.",
    contactsSoonCargo:
      "Yük sahibi olarak giriş yaptınız",
    contactsSoonExpeditor:
      "Ödeme henüz kullanılamıyor",

    addTitle:
      "Yeni yük",
    editTitle:
      "Yükü düzenle",
    step:
      "Adım",
    routeStep:
      "Rota",
    cargoStep:
      "Yük ve araç",
    priceStep:
      "Fiyat ve ödeme",
    next:
      "Devam",
    previous:
      "Geri",
    publish:
      "Ücretsiz yayınla",
    save:
      "Kaydet",
    publishing:
      "Kaydediliyor…",
    freePublish:
      "Yük ilanı vermek ücretsizdir.",

    routeRequired:
      "Başlangıç ve varış noktalarını girin.",
    sameRoute:
      "Başlangıç ve varış aynı olamaz.",
    cargoRequired:
      "Yükü girin.",
    weightRequired:
      "Ağırlığı girin.",
    truckRequired:
      "Araç tipi seçin.",
    loadingRequired:
      "Yükleme tarihini girin veya yükün hazır olduğunu işaretleyin.",
    priceRequired:
      "Sabit fiyat için tutarı girin.",

    cargoName:
      "Yük adı",
    cargoPlaceholder:
      "Örn. meyve, ekipman",
    weightTons:
      "Ağırlık, ton",
    volumeM3:
      "Hacim, m³",
    truckType:
      "Araç tipi",
    truckCount:
      "Araç sayısı",
    loading:
      "Yükleme",
    exactDate:
      "Tarihli",
    readyNow:
      "Yük hazır",

    priceMode:
      "Fiyat",
    fixed:
      "Sabit",
    currency:
      "Para birimi",
    advance:
      "Avans, %",
    payment:
      "Ödeme",
    paymentChoose:
      "Ödeme yöntemini seçin",
    paymentRequired:
      "Ödeme yöntemini seçin.",
    cash:
      "Nakit",
    card:
      "Transfer",
    bank:
      "Banka havalesi",
    comment:
      "Not",
    commentPlaceholder:
      "Yükleme koşulları, yük özellikleri…",

    phoneFromAccount:
      "İletişim",
    phoneAuto:
      "Telefon numarası hesaptan otomatik alınır.",

    published:
      "Yük yayınlandı.",
    updated:
      "Değişiklikler kaydedildi.",
    requestFailed:
      "İstek tamamlanamadı.",

    active:
      "Aktif",
    history:
      "Geçmiş",
    noMine:
      "Henüz aktif yükünüz yok.",
    noHistory:
      "Geçmiş henüz boş.",
    edit:
      "Düzenle",
    close:
      "Bitir",
    closeConfirm:
      "Bu yük ilanını bitirmek istiyor musunuz?",
    closeCancel:
      "İptal",
    closed:
      "Bitti",
    activeStatus:
      "Aktif",
  },
};

function languageCode() {
  const raw =
    String(
      i18next.resolvedLanguage ||
      i18next.language ||
      "ru"
    )
      .toLowerCase()
      .slice(0, 2);

  return COPY[raw]
    ? raw
    : "ru";
}

function formatBoardDateTime(value, lang = "ru") {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function boardFreightOriginAt(item) {
  return item?.firstSeenAt || item?.lastSeenAt || null;
}

function boardFreightAgeHours(item, nowMs = Date.now()) {
  const raw = boardFreightOriginAt(item);

  if (!raw) {
    return null;
  }

  const ms =
    new Date(raw).getTime();

  if (!Number.isFinite(ms)) {
    return null;
  }

  return Math.max(
    0,
    (nowMs - ms) /
      (60 * 60 * 1000)
  );
}

function boardFreightAgeToneClass(item) {
  const age =
    boardFreightAgeHours(item);

  if (age == null) {
    return "reisor-freight-age--unknown";
  }

  if (age < 3) {
    return "reisor-freight-age--fresh";
  }

  if (age < 7) {
    return "reisor-freight-age--aging";
  }

  return "reisor-freight-age--old";
}

function marketLegalUrl(kind, role, lang) {
  const scope = role === "EXPEDITOR" ? "expeditor" : "client";
  const safeLang = ["ru", "uz", "tr"].includes(lang) ? lang : "ru";
  const base = "https://backend.cargo-platform.online/api/public/legal";
  if (kind === "offer") return `${base}/offer/print?scope=${encodeURIComponent(scope)}&lang=${encodeURIComponent(safeLang)}`;
  if (kind === "paid-access") return `${base}/paid-access/print?scope=${encodeURIComponent(scope)}&lang=${encodeURIComponent(safeLang)}`;
  if (kind === "privacy") return `${base}/privacy/print?lang=${encodeURIComponent(safeLang)}`;
  if (kind === "data-usage") return `${base}/data-usage/print?lang=${encodeURIComponent(safeLang)}`;
  if (kind === "entities") return `${base}/entities/print?lang=${encodeURIComponent(safeLang)}`;
  return "";
}

function asText(value) {
  return String(
    value == null
      ? ""
      : value
  ).trim();
}

function reisorCargoPriorityUntilMs(item) {
  const raw =
    item?.reisorCargoPriorityUntil;

  if (!raw) return 0;

  const ms =
    new Date(raw).getTime();

  return Number.isFinite(ms)
    ? ms
    : 0;
}

function isReisorCargoPriorityActive(
  item,
  nowMs = Date.now()
) {
  return Boolean(
    item?.reisorProNative &&
    reisorCargoPriorityUntilMs(item) >
      nowMs
  );
}

function routePlace(
  value,
  geo
) {
  const raw =
    asText(value)
      .replace(
        /\s+/g,
        " "
      )
      .trim();

  if (!raw) {
    return "—";
  }

  const code =
    asText(
      geo?.countryCode ||
      geo?.country_code
    )
      .toUpperCase()
      .trim();

  const city =
    raw
      .replace(
        /\s*,\s*[A-Z]{2}$/i,
        ""
      )
      .replace(
        /\s+[A-Z]{2}$/i,
        ""
      )
      .split(",")[0]
      .trim() ||
    raw;

  return /^[A-Z]{2}$/.test(code)
    ? `${city}, ${code}`
    : city;
}

function expeditorRoutePlace(
  value,
  canonical = ""
) {
  const display =
    marketDisplayPlace(
      value,
      "ru",
      canonical
    );

  return display;
}

function localDateIso(addDays = 0) {
  const date =
    new Date();

  date.setHours(
    12,
    0,
    0,
    0
  );

  date.setDate(
    date.getDate() +
      addDays
  );

  const y =
    date.getFullYear();

  const m =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const d =
    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${y}-${m}-${d}`;
}

function formatNumber(value) {
  const number =
    Number(value);

  if (
    !Number.isFinite(
      number
    )
  ) {
    return "";
  }

  return new Intl
    .NumberFormat()
    .format(number);
}

function formatWeightKg(value) {
  const kg =
    Number(value);

  if (
    !Number.isFinite(kg) ||
    kg <= 0
  ) {
    return "—";
  }

  const tons =
    kg / 1000;

  return `${new Intl.NumberFormat(
    undefined,
    {
      maximumFractionDigits: 1,
    }
  ).format(tons)} t`;
}

const TRUCK_LABELS = {
  ru: {
    TENT: "Тент", REF: "Рефрижератор", ISOTHERM: "Изотерм",
    BOX: "Фургон", CONTAINER: "Контейнеровоз", PLATFORM: "Платформа",
    MEGA: "Mega", PARAVOZ: "Паровоз", ISUZU: "Isuzu", BONGO: "Bongo",
    SAMOSVAL: "Самосвал", GAZEL: "Газель",
  },
  uz: {
    TENT: "Тент", REF: "Рефрижератор", ISOTHERM: "Изотерм",
    BOX: "Фургон", CONTAINER: "Контейнер ташувчи", PLATFORM: "Платформа",
    MEGA: "Mega", PARAVOZ: "Паровоз", ISUZU: "Isuzu", BONGO: "Bongo",
    SAMOSVAL: "Самосвал", GAZEL: "Газель",
  },
  tr: {
    TENT: "Tenteli", REF: "Frigorifik", ISOTHERM: "İzoterm",
    BOX: "Kapalı kasa", CONTAINER: "Konteyner taşıyıcı", PLATFORM: "Platform",
    MEGA: "Mega", PARAVOZ: "Römorklu", ISUZU: "Isuzu", BONGO: "Bongo",
    SAMOSVAL: "Damperli", GAZEL: "Gazelle",
  },
};

function truckLabel(value) {
  const raw = asText(value).trim().toUpperCase();
  if (!raw) return "";

  const localized =
    TRUCK_LABELS[languageCode()]?.[raw] ||
    TRUCK_LABELS.ru?.[raw];

  if (localized) return localized;

  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (part) => part.toUpperCase());
}

function boardFreightContactDraft(item, lang = "ru") {
  const from =
    expeditorRoutePlace(
      item?.from,
      item?.routeFromCanonical || item?.fromCanonical
    ) || "";

  const to =
    expeditorRoutePlace(
      item?.to,
      item?.routeToCanonical || item?.toCanonical
    ) || "";

  const truck = Array.isArray(item?.truckTypes)
    ? item.truckTypes
        .map(truckLabel)
        .filter(Boolean)
        .join(" · ")
    : "";

  return buildFreightContactDraft({
    from,
    to,
    truck,
    language: lang,
  });
}

function marketCargo(item) {
  return (
    asText(
      item?.cargoDisplay
    ) ||
    truckLabel(
      item?.cargo
    ) ||
    "—"
  );
}

function marketLoading(item) {
  if (
    item?.loadingReady
  ) {
    return "✓";
  }

  return (
    asText(
      item?.loadingDisplay
    ) ||
    asText(
      item?.loadingDate
    ) ||
    "—"
  );
}

function marketRate(
  item,
  t
) {
  return (
    asText(
      item?.ratePrimary
    ) ||
    asText(
      item?.rateHint
    ) ||
    asText(
      item?.rateSecondary
    ) ||
    t.negotiable
  );
}

async function publicRequest(
  path,
  options = {}
) {
  const {
    response,
    data,
  } =
    await reisorProDriverRequest(
      path,
      options
    );

  if (!response.ok) {
    const error =
      new Error(
        data?.error ||
        `HTTP_${response.status}`
      );

    error.status =
      response.status;

    error.data =
      data;

    throw error;
  }

  return data;
}

async function boardRequest(
  path,
  {
    token,
    method = "GET",
    body,
  } = {}
) {
  const { response, data } =
    await reisorProMarketRequest(
      path,
      { token, method, body }
    );

  if (!response.ok) {
    const error =
      new Error(
        data?.error ||
        `HTTP_${response.status}`
      );

    error.status =
      response.status;

    error.data =
      data;

    throw error;
  }

  return data;
}

function marketRequestErrorText(
  error,
  lang,
  t
) {
  const code = asText(
    error?.data?.error ||
    error?.message
  ).toUpperCase();

  const direct = {
    ROUTE_REQUIRED:
      t.routeRequired,

    ROUTE_SAME_POINT:
      t.sameRoute,

    CARGO_REQUIRED:
      t.cargoRequired,

    TRUCK_TYPE_REQUIRED:
      t.truckRequired,

    LOADING_REQUIRED:
      t.loadingRequired,

    BAD_PRICE:
      t.priceRequired,
  };

  if (direct[code]) {
    return direct[code];
  }

  const extra = {
    ru: {
      BAD_GEO:
        "Проверьте пункт отправления и назначения.",

      BAD_LOADING_DATE:
        "Проверьте дату загрузки.",

      BAD_WEIGHT:
        "Вес должен быть от 0,1 до 1000 тонн.",

      BAD_VOLUME:
        "Объём должен быть от 0 до 10000 м³.",

      BAD_TRUCK_COUNT:
        "Количество машин должно быть от 1 до 1000.",

      BAD_ADVANCE:
        "Аванс должен быть от 0 до 100%.",

      MARKET_PHONE_REQUIRED:
        "Не найден телефон аккаунта. Выйдите и войдите снова.",

      MARKET_ROLE_REQUIRED:
        "Сессия REISOR PRO устарела. Выйдите и войдите снова.",
    },

    uz: {
      BAD_GEO:
        "Юклаш ва тушириш жойини текширинг.",

      BAD_LOADING_DATE:
        "Юклаш санасини текширинг.",

      BAD_WEIGHT:
        "Оғирлик 0,1 дан 1000 тоннагача бўлиши керак.",

      BAD_VOLUME:
        "Ҳажм 0 дан 10000 м³ гача бўлиши керак.",

      BAD_TRUCK_COUNT:
        "Машина сони 1 дан 1000 гача бўлиши керак.",

      BAD_ADVANCE:
        "Аванс 0 дан 100% гача бўлиши керак.",

      MARKET_PHONE_REQUIRED:
        "Аккаунт телефон рақами топилмади. Чиқиб, қайта киринг.",

      MARKET_ROLE_REQUIRED:
        "REISOR PRO сессияси эскирган. Чиқиб, қайта киринг.",
    },

    tr: {
      BAD_GEO:
        "Başlangıç ve varış noktalarını kontrol edin.",

      BAD_LOADING_DATE:
        "Yükleme tarihini kontrol edin.",

      BAD_WEIGHT:
        "Ağırlık 0,1 ile 1000 ton arasında olmalıdır.",

      BAD_VOLUME:
        "Hacim 0 ile 10000 m³ arasında olmalıdır.",

      BAD_TRUCK_COUNT:
        "Araç sayısı 1 ile 1000 arasında olmalıdır.",

      BAD_ADVANCE:
        "Avans 0 ile 100% arasında olmalıdır.",

      MARKET_PHONE_REQUIRED:
        "Hesap telefonu bulunamadı. Çıkış yapıp tekrar giriş yapın.",

      MARKET_ROLE_REQUIRED:
        "REISOR PRO oturumu eskimiş. Çıkış yapıp tekrar giriş yapın.",
    },
  };

  if (extra[lang]?.[code]) {
    return extra[lang][code];
  }

  return /^[A-Z0-9_]{2,64}$/.test(code)
    ? `${t.requestFailed} (${code})`
    : t.requestFailed;
}

function emptyGeo() {
  return {
    label: "",
    countryCode: "",
    lat: null,
    lon: null,
  };
}

/* REISOR_PRO_PAYMENT_TYPES_3_V1 */

function normalizePaymentType(value) {
  const raw =
    asText(value)
      .toUpperCase();

  return [
    "CASH",
    "CARD",
    "BANK_TRANSFER",
  ].includes(raw)
    ? raw
    : "";
}

const MARKET_DRAFT_PREFIX =
  "reisor_pro_market_draft_v1";

function marketDraftKey(
  role,
  user
) {
  const normalizedRole =
    asText(role).toUpperCase();
  const userId =
    asText(user?.id);

  if (
    !userId ||
    ![
      "CARGO",
      "EXPEDITOR",
    ].includes(normalizedRole)
  ) {
    return "";
  }

  return `${MARKET_DRAFT_PREFIX}:${normalizedRole}:${userId}`;
}

function emptyDraft() {
  return {
    from: "",
    to: "",
    fromGeo:
      emptyGeo(),
    toGeo:
      emptyGeo(),

    cargo: "",
    weightTons: "",
    volumeM3: "",
    truckTypes: [],
    requiredTrucks: "1",

    loadingDate: "",
    loadingReady: false,

    priceMode:
      "NEGOTIABLE",
    priceAmount: "",
    currency:
      "UZS",
    advancePercent:
      "0",
    paymentType:
      "",

    comment: "",
  };
}

function readMarketDraft(
  role,
  user
) {
  const key =
    marketDraftKey(
      role,
      user
    );

  if (!key) {
    return null;
  }

  try {
    const raw =
      localStorage.getItem(
        key
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed !==
        "object" ||
      !parsed.draft ||
      typeof parsed.draft !==
        "object"
    ) {
      return null;
    }

    const restored = {
      ...emptyDraft(),
      ...parsed.draft,
      fromGeo: {
        ...emptyGeo(),
        ...(parsed.draft
          .fromGeo || {}),
      },
      toGeo: {
        ...emptyGeo(),
        ...(parsed.draft
          .toGeo || {}),
      },
      truckTypes:
        Array.isArray(
          parsed.draft
            .truckTypes
        )
          ? parsed.draft
              .truckTypes
          : [],
    };

    const restoredStep =
      Number(parsed.step);

    const restoredView =
      [
        "market",
        "add",
        "mine",
        "vehicles",
        "park",
        "settings",
      ].includes(
        parsed.view
      )
        ? parsed.view
        : "market";

    return {
      draft: restored,
      step:
        [1, 2, 3].includes(
          restoredStep
        )
          ? restoredStep
          : 1,
      view:
        restoredView,
    };
  } catch {
    return null;
  }
}

function saveMarketDraft(
  role,
  user,
  state
) {
  const key =
    marketDraftKey(
      role,
      user
    );

  if (!key) {
    return;
  }

  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        draft:
          state?.draft ||
          emptyDraft(),
        step:
          [1, 2, 3].includes(
            Number(state?.step)
          )
            ? Number(state.step)
            : 1,
        view:
          [
            "market",
            "add",
            "mine",
            "vehicles",
            "park",
            "starHub",
            "settings",
          ].includes(
            state?.view
          )
            ? state.view
            : "market",
        savedAt:
          Date.now(),
      })
    );
  } catch {}
}

function clearMarketDraft(
  role,
  user
) {
  const key =
    marketDraftKey(
      role,
      user
    );

  if (!key) {
    return;
  }

  try {
    localStorage.removeItem(
      key
    );
  } catch {}
}

function postToDraft(post) {
  return {
    from:
      asText(post?.from),
    to:
      asText(post?.to),

    fromGeo: {
      ...emptyGeo(),
      ...(
        post?.fromGeo ||
        {}
      ),
    },

    toGeo: {
      ...emptyGeo(),
      ...(
        post?.toGeo ||
        {}
      ),
    },

    cargo:
      asText(post?.cargo),

    weightTons:
      post?.weightTons == null
        ? ""
        : String(
            post.weightTons
          ),

    volumeM3:
      post?.volumeM3 == null
        ? ""
        : String(
            post.volumeM3
          ),

    truckTypes:
      Array.isArray(
        post?.truckTypes
      )
        ? post.truckTypes
        : [],

    requiredTrucks:
      String(
        post?.requiredTrucks ||
        1
      ),

    loadingDate:
      asText(
        post?.loadingDate
      ),

    loadingReady:
      Boolean(
        post?.loadingReady
      ),

    priceMode:
      asText(
        post?.priceMode
      ) ||
      "NEGOTIABLE",

    priceAmount:
      post?.priceAmount == null
        ? ""
        : String(
            post.priceAmount
          ),

    currency:
      asText(
        post?.currency
      ) ||
      "UZS",

    advancePercent:
      String(
        post?.advancePercent ??
        0
      ),

    paymentType:
      normalizePaymentType(
        post?.paymentType
      ),

    comment:
      asText(
        post?.comment
      ),
  };
}

function GeoField({
  label,
  placeholder,
  value,
  geo,
  onChange,
}) {
  const [
    suggestions,
    setSuggestions,
  ] = useState([]);

  const [busy, setBusy] =
    useState(false);

  const abortRef =
    useRef(null);

  const skipRef =
    useRef(false);

  useEffect(() => {
    const query =
      asText(value);

    if (
      skipRef.current
    ) {
      skipRef.current =
        false;
      return undefined;
    }

    if (
      abortRef.current
    ) {
      abortRef.current
        .abort();
    }

    if (
      query.length < 3
    ) {
      setSuggestions([]);
      return undefined;
    }

    const controller =
      new AbortController();

    abortRef.current =
      controller;

    const timer =
      window.setTimeout(
        async () => {
          setBusy(true);

          try {
            const {
              response,
              data,
            } =
              await reisorProDriverRequest(
                "/api/geo/suggest",
                {
                  method:
                    "POST",

                  body: {
                    query,
                  },

                  signal:
                    controller.signal,
                }
              );

            if (
              !controller
                .signal
                .aborted
            ) {
              setSuggestions(
                Array.isArray(
                  data?.items
                )
                  ? data.items
                      .slice(
                        0,
                        7
                      )
                  : []
              );
            }
          } catch (error) {
            if (
              error?.name !==
              "AbortError"
            ) {
              setSuggestions([]);
            }
          } finally {
            if (
              !controller
                .signal
                .aborted
            ) {
              setBusy(false);
            }
          }
        },
        350
      );

    return () => {
      window.clearTimeout(
        timer
      );

      controller.abort();
    };
  }, [value]);

  return (
    <div className="rpb-geo">
      <label>
        {label}
      </label>

      <div className="rpb-geo__input-wrap">
        <MapPin
          size={18}
          aria-hidden="true"
        />

        <input
          value={value}
          onChange={(event) => {
            const text =
              event.target.value;

            onChange(
              text,
              {
                ...emptyGeo(),
                label:
                  text,
              }
            );
          }}
          placeholder={
            placeholder
          }
          autoComplete="off"
        />

        {busy ? (
          <Loader2
            size={17}
            className="rpb-spin"
            aria-hidden="true"
          />
        ) : null}
      </div>

      {suggestions.length ? (
        <div className="rpb-geo__suggestions">
          {suggestions.map(
            (
              item,
              index
            ) => {
              const itemValue =
                asText(
                  item?.value
                );

              return (
                <button
                  type="button"
                  key={
                    `${itemValue}-${index}`
                  }
                  onMouseDown={(
                    event
                  ) => {
                    event
                      .preventDefault();

                    skipRef.current =
                      true;

                    const selectedGeo = {
                      label:
                        itemValue,

                      countryCode:
                        asText(
                          item?.countryCode
                        )
                          .toUpperCase()
                          .slice(
                            0,
                            2
                          ),

                      lat:
                        item?.geo
                          ?.lat ??
                        null,

                      lon:
                        item?.geo
                          ?.lon ??
                        null,
                    };

                    onChange(
                      itemValue,
                      selectedGeo
                    );

                    setSuggestions(
                      []
                    );
                  }}
                >
                  <MapPin
                    size={16}
                    aria-hidden="true"
                  />

                  <span>
                    {itemValue}
                  </span>
                </button>
              );
            }
          )}
        </div>
      ) : null}

      {geo?.countryCode ? (
        <small className="rpb-geo__selected">
          <Check
            size={13}
            aria-hidden="true"
          />

          {geo.countryCode}
        </small>
      ) : null}
    </div>
  );
}

function Header({
  t,
  roleTitle,
  phone,
  isExpeditor,
  view,
  onBack,
  onLogout,
  onView,
  onQuickCreate,
}) {
  return (
    <>
      <header className="rpb-header">
        <button
          type="button"
          className="rpb-icon-button"
          onClick={onBack}
          aria-label={
            t.back
          }
        >
          <ArrowLeft
            size={20}
            aria-hidden="true"
          />
        </button>

        <div className="rpb-header__brand">
          <div>
            <strong>
              REISOR
              <span>
                PRO
              </span>
            </strong>

            <small>
              {roleTitle}
            </small>
          </div>

          <div className="rpb-header__actions">
            <button
              type="button"
              className="rpb-logout"
              onClick={onLogout}
            >
              <LogOut
                size={17}
                aria-hidden="true"
              />

              <span>
                {t.logout}
              </span>
            </button>
          </div>
        </div>
      </header>

      <div className="rpb-phone-line">
        {phone || ""}
      </div>

      {!isExpeditor ? (
        <button
          type="button"
          className="rpb-support-bar rpb-support-bar--cargo"
          onClick={
            openReisorProSupport
          }
        >
          <span
            className="rpb-support-bar__icon"
            aria-hidden="true"
          >
            ?
          </span>

          <span>
            {t.support}
          </span>
        </button>
      ) : null}

      <nav className={isExpeditor ? "rpb-main-tabs rpb-main-tabs--expeditor" : "rpb-main-tabs rpb-main-tabs--cargo"}>
        <button
          type="button"
          className={
            view === "market"
              ? "is-active"
              : ""
          }
          onClick={() =>
            onView(
              "market"
            )
          }
        >
          <Boxes
            size={18}
            aria-hidden="true"
          />

          {isExpeditor ? t.expeditorLoadsTitle : t.market}
        </button>

        <button
          type="button"
          className={view === "vehicles" ? "is-active" : ""}
          onClick={() => onView("vehicles")}
        >
          <Truck size={18} aria-hidden="true" />
          {t.vehiclesNav}
        </button>

        {!isExpeditor ? (
          <button
            type="button"
            className={view === "mine" ? "is-active" : ""}
            onClick={() => onView("mine")}
          >
            <ClipboardList size={18} aria-hidden="true" />
            {t.mine}
          </button>
        ) : null}

        {isExpeditor ? (
          <>
            <button
              type="button"
              className="rpb-main-tabs__create"
              onClick={onQuickCreate}
              aria-label={t.quickCreate}
            >
              <Plus size={27} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={
                ["starHub", "myVehicles", "park"].includes(view)
                  ? "is-active"
                  : ""
              }
              onClick={() => onView("starHub")}
              aria-label={t.starHubTitle}
              title={t.starHubTitle}
              data-reisor-expeditor-star-hub-nav="v1"
            >
              <span
                className="text-[22px] leading-none"
                aria-hidden="true"
              >
                ⭐
              </span>
            </button>
            <button
              type="button"
              className={`rpb-main-tabs__settings${view === "settings" ? " is-active" : ""}`}
              onClick={() => onView("settings")}
              aria-label={t.settingsNav}
              title={t.settingsNav}
            >
              <Settings size={23} aria-hidden="true" />
            </button>
          </>
        ) : null}
      </nav>
    </>
  );
}

function RoleSettingsPanel({
  t,
  role,
  roleTitle,
  user,
  lang,
  pushState,
  onEnableNotifications,
  onOpenLegal,
}) {
  const isExpeditor = role === "EXPEDITOR";
  const [instructionOpen, setInstructionOpen] = useState(false);
  const instructionMeta = expeditorInstructionMeta(lang);
  const createdAt = formatBoardDateTime(user?.createdAt, lang);
  const status = asText(user?.status || "ACTIVE");
  const statusLabel = status === "ACTIVE" ? t.activeStatus : status;

  if (isExpeditor && instructionOpen) {
    return (
      <ReisorProExpeditorInstruction
        lang={lang}
        pushState={pushState}
        onEnableNotifications={onEnableNotifications}
        onBack={() => setInstructionOpen(false)}
      />
    );
  }

  return (
    <section className="rpb-role-settings" data-reisor-role-settings="m5a-v1">
      <div className="rpb-title-row">
        <div><h1>{t.settingsNav}</h1></div>
      </div>

      <div className="rpb-settings-card">
        <div className="rpb-settings-card__title">{t.profile}</div>
        <div className="rpb-settings-profile-grid">
          <span><small>{t.accountPhone}</small><strong>{asText(user?.phone) || "—"}</strong></span>
          <span><small>{t.accountRole}</small><strong>{roleTitle}</strong></span>
          <span><small>{t.accountStatus}</small><strong>{statusLabel}</strong></span>
          <span><small>{t.accountCreated}</small><strong>{createdAt}</strong></span>
        </div>
      </div>

      {isExpeditor ? (
        <div className="rpb-settings-card">
          <div className="rpb-settings-card__title">REISOR</div>
          <button type="button" className="rpb-settings-row rpb-settings-row--instruction" onClick={() => setInstructionOpen(true)}>
            <span className="rpb-settings-row__copy">
              <strong>{instructionMeta.title}</strong>
              <small>{instructionMeta.description}</small>
            </span>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <div className="rpb-settings-card">
        <div className="rpb-settings-card__title">{t.documentsRules}</div>
        <button type="button" className="rpb-settings-row" onClick={() => onOpenLegal("offer", t.offer)}>
          <span>{t.offer}</span><ChevronRight size={18} aria-hidden="true" />
        </button>
        {isExpeditor ? (
          <button type="button" className="rpb-settings-row" onClick={() => onOpenLegal("paid-access", t.paidAccess)}>
            <span>{t.paidAccess}</span><ChevronRight size={18} aria-hidden="true" />
          </button>
        ) : null}
        <button type="button" className="rpb-settings-row" onClick={() => onOpenLegal("privacy", t.privacy)}>
          <span>{t.privacy}</span><ChevronRight size={18} aria-hidden="true" />
        </button>
        <button type="button" className="rpb-settings-row" onClick={() => onOpenLegal("data-usage", t.dataUsage)}>
          <span>{t.dataUsage}</span><ChevronRight size={18} aria-hidden="true" />
        </button>
        <button type="button" className="rpb-settings-row" onClick={() => onOpenLegal("entities", t.entities)}>
          <span>{t.entities}</span><ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>

      {isExpeditor ? (
        <div className="rpb-settings-card">
          <div className="rpb-settings-card__title">{t.notifications}</div>
          <button type="button" className="rpb-settings-row" disabled={pushState?.busy} onClick={onEnableNotifications}>
            <span>{pushState?.busy ? "…" : t.enableNotifications}</span><Bell size={18} aria-hidden="true" />
          </button>
          {pushState?.message ? <div className={`rpb-settings-message${pushState?.ok ? " is-ok" : ""}`}>{pushState.message}</div> : null}
        </div>
      ) : null}

      <div className="rpb-settings-card">
        <div className="rpb-settings-card__title">{t.appearance}</div>
        <LanguageSelector />
        <ReisorProThemeToggle />
      </div>

      <button type="button" className="rpb-settings-support" onClick={openReisorProSupport}>
        <span>{t.support}</span><ChevronRight size={18} aria-hidden="true" />
      </button>
    </section>
  );
}

function CargoBottomNav({ t, view, onView }) {
  return (
    <nav className="rpb-cargo-bottom-nav" aria-label={t.settingsNav} data-reisor-cargo-bottom-nav="m5a-v1">
      <button type="button" className={view === "market" ? "is-active" : ""} onClick={() => onView("market")}>
        <Boxes size={20} aria-hidden="true" /><span>{t.cargoLoadsNav}</span>
      </button>
      <button type="button" className={view === "vehicles" ? "is-active" : ""} onClick={() => onView("vehicles")}>
        <Truck size={20} aria-hidden="true" /><span>{t.vehiclesNav}</span>
      </button>
      <button type="button" className={view === "mine" ? "is-active" : ""} onClick={() => onView("mine")}>
        <ClipboardList size={20} aria-hidden="true" /><span>{t.mine}</span>
      </button>
      <button type="button" className={view === "settings" ? "is-active" : ""} onClick={() => onView("settings")} aria-label={t.settingsNav}>
        <Settings size={21} aria-hidden="true" /><span>{t.settingsNav}</span>
      </button>
    </nav>
  );
}

function marketBoardScrollHost() {
  return document.querySelector(".page-scroll");
}

const MARKET_EXPEDITOR_FILTER_UI_KEY =
  "reisor_market_expeditor_filter_ui_v1";
const MARKET_EXPEDITOR_DEEP_FREIGHT_KEY =
  "reisor_pro_market_expeditor_deep_freight_id";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

function marketSavedSearchItems(data) {
  return Array.isArray(data?.items) ? data.items : [];
}

function readMarketExpeditorNativePushToken() {
  for (const key of [
    "driver.push.token.pending",
    "driver.push.token.lastSent",
  ]) {
    try {
      const value =
        asText(
          localStorage.getItem(key)
        );

      if (value) {
        return value;
      }
    } catch {}
  }

  return "";
}

function readMarketExpeditorFilterUi() {
  try {
    const raw =
      sessionStorage.getItem(
        MARKET_EXPEDITOR_FILTER_UI_KEY
      );

    const parsed =
      raw
        ? JSON.parse(raw)
        : {};

    return (
      parsed &&
      typeof parsed === "object"
    )
      ? parsed
      : {};
  } catch {
    return {};
  }
}

// REISOR_STAGE156B_MARKET_EXPEDITOR_DRIVER_FILTER_PARITY_V1
export default function
ReisorProMarketBoard({
  role,
  roleTitle,
  marketToken,
  user,
  onBack,
  onLogout,
}) {
  useTranslation();

  const lang =
    languageCode();

  const t =
    COPY[lang];

  const effectiveRoleTitle =
    roleTitle ||
    (
      role ===
        "EXPEDITOR"
        ? t.expeditorRole
        : t.cargoRole
    );

  const isExpeditor = role === "EXPEDITOR";
  const aiFloatingPill = useDraggableFloatingPill(
    isExpeditor
      ? "reisor.ai.floating.expeditor.v1"
      : "reisor.ai.floating.cargo.v1"
  );
  const [aiOpen, setAiOpen] = useState(false); // REISOR_AI_V04_REISOR_PRO_EXPEDITOR
  const [aiEntryIntent, setAiEntryIntent] = useState(""); // REISOR_STAGE163M5C4_EXPEDITOR_AI_ACTIVATION_V1
  const [roleAiEntryOpen, setRoleAiEntryOpen] = useState(false); // REISOR_STAGE163M5C2_ROLE_AI_ENTRY_V1
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);
  const [pushSettingsState, setPushSettingsState] = useState({ busy: false, ok: false, message: "" });

  const [
    vehicleSearchPreset,
    setVehicleSearchPreset,
  ] = useState(null);

  const [vehicleDiscoveryMode, setVehicleDiscoveryMode] = useState("vehicles");
  const [vehicleResumeTarget, setVehicleResumeTarget] = useState(null);
  const [pendingDriverInvite, setPendingDriverInvite] = useState(null);
  const [pendingVehicleInvite, setPendingVehicleInvite] = useState(null);
  const [driverInviteNotice, setDriverInviteNotice] = useState(null);

  const [
    aiVehicleDraft,
    setAiVehicleDraft,
  ] = useState(null);

  function prepareAiExpeditorVehicleDraft(
    draft = {}
  ) {
    if (!isExpeditor) {
      return;
    }

    const capacityRaw =
      draft?.capacityTons;

    const capacity =
      capacityRaw == null ||
      capacityRaw === ""
        ? null
        : Number(capacityRaw);

    const volumeRaw =
      draft?.volumeM3;

    const volume =
      volumeRaw == null ||
      volumeRaw === ""
        ? null
        : Number(volumeRaw);

    setAiVehicleDraft({
      aiDraft: true,

      from:
        asText(draft?.from),

      to:
        asText(draft?.to),

      bodyType:
        asText(
          draft?.bodyType ||
          draft?.truck
        ),

      capacityTons:
        Number.isFinite(capacity) &&
        capacity > 0
          ? capacity
          : null,

      volumeM3:
        Number.isFinite(volume) &&
        volume >= 0
          ? volume
          : null,

      partialLoad:
        Boolean(
          draft?.partialLoad
        ),

      availableFrom:
        asText(
          draft?.availableFrom ||
          draft?.readyAt
        ),

      priceMode:
        "NEGOTIABLE",

      priceAmount:
        null,

      currency:
        "UZS",

      comment:
        "",
    });

    setAiOpen(false);
  }

  function prepareParkVehiclePublication(
    item = {}
  ) {
    if (!isExpeditor) {
      return;
    }

    // IMPORTANT:
    // Park row ID is intentionally NOT copied.
    // Otherwise the public vehicle modal could enter edit mode.
    //
    // Private Park contact fields are intentionally NOT copied:
    // fullName, phone, telegramUsername, maxContact,
    // plateNumber and private comment.

    setAiVehicleDraft({
      parkDraft: true,

      // Park knows where the vehicle will be available.
      from:
        asText(
          item?.availableLocation
        ),

      // Park does NOT know the public destination.
      // User must explicitly provide it.
      to: "",

      // Pass through current REISOR bodyType unchanged.
      bodyType:
        asText(
          item?.bodyType
        ),

      // Park does NOT currently store these public-market values.
      // Never invent them.
      capacityTons: null,
      volumeM3: null,
      partialLoad: false,

      availableFrom:
        asText(
          item?.availableAt
        ),

      priceMode:
        "NEGOTIABLE",

      priceAmount:
        null,

      currency:
        "UZS",

      // Private Park comment must not leak to public market.
      comment: "",

      carBrand:
        asText(
          item?.carBrand
        ),

      carModel:
        asText(
          item?.carModel
        ),

      carYear: "",
    });

    setAiOpen(false);
  }

  // REISOR_PRO_EXPEDITOR_MARKET_STALE_RESPONSE_GUARD_V1
  // Latest market request wins, matching the proven DRIVER behaviour.
  const marketLoadSeqRef = useRef(0);
  const marketNextOffsetRef = useRef(0);

  const restoredNewDraft =
    useMemo(
      () =>
        readMarketDraft(
          role,
          user
        ),
      [role, user?.id]
    );
  const [view, setView] =
    useState(
      () =>
        restoredNewDraft
          ?.view ||
        "market"
    );

  useEffect(() => {
    const uid = asText(user?.id || user?._id || "anon");
    const key = `reisor_pro_role_ai_entry_seen_${role}_${uid}`;
    let seen = false;
    try { seen = sessionStorage.getItem(key) === "1"; } catch {}
    if (!seen) setRoleAiEntryOpen(true);
  }, [role, user?.id, user?._id]);

  const closeRoleAiEntry = () => {
    const uid = asText(user?.id || user?._id || "anon");
    try { sessionStorage.setItem(`reisor_pro_role_ai_entry_seen_${role}_${uid}`, "1"); } catch {}
    setRoleAiEntryOpen(false);
  };

  const [
    marketItems,
    setMarketItems,
  ] = useState([]);

  const [
    marketTotal,
    setMarketTotal,
  ] = useState(0);

  // Cargo-owner headline count is independent from the current market list.
  // marketTotal remains the list/pagination/filter total for both roles.
  const [
    marketTotal24h,
    setMarketTotal24h,
  ] = useState(0);

  const [
    marketBusy,
    setMarketBusy,
  ] = useState(false);

  const [
    marketMoreBusy,
    setMarketMoreBusy,
  ] = useState(false);

  const [
    marketError,
    setMarketError,
  ] = useState("");

  const [
    marketDetail,
    setMarketDetail,
  ] = useState(null);

  const [
    filtersOpen,
    setFiltersOpen,
  ] = useState(false);

  const [
    filterCompact,
    setFilterCompact,
  ] = useState(false);

  // EXPEDITOR search window mirrors Driver: 24h by default; 6h is an optional fresh-only filter.
  const [
    marketAgeWindowHours,
    setMarketAgeWindowHours,
  ] = useState(24);

  const [
    marketPriorityTick,
    setMarketPriorityTick,
  ] = useState(0);

  const [
    filters,
    setFilters,
  ] = useState(() => {
    const saved =
      isExpeditor
        ? readMarketExpeditorFilterUi()
        : {};

    return {
      from:
        asText(
          saved?.filters?.from
        ),

      to:
        asText(
          saved?.filters?.to
        ),

      truck:
        asText(
          saved?.filters?.truck
        ),

      dateMode:
        [
          "today",
          "tomorrow",
        ].includes(
          asText(
            saved?.filters?.dateMode
          )
        )
          ? asText(
              saved.filters.dateMode
            )
          : "all",
    };
  });

  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFiltersError, setSavedFiltersError] = useState("");
  const [savedFilterBusyId, setSavedFilterBusyId] = useState("");
  const [savedSearchState, setSavedSearchState] = useState({
    key: "",
    status: "idle",
    message: "",
  });

  useEffect(() => {
    if (!isExpeditor) {
      return;
    }

    try {
      sessionStorage.setItem(
        MARKET_EXPEDITOR_FILTER_UI_KEY,
        JSON.stringify({
          filters,
          ageWindowHours: marketAgeWindowHours,
        })
      );
    } catch {}
  }, [
    isExpeditor,
    filters,
    marketAgeWindowHours,
  ]);

  useEffect(() => {
    if (
      !isExpeditor ||
      view !== "market"
    ) {
      return undefined;
    }

    const host =
      marketBoardScrollHost();

    if (!host) {
      return undefined;
    }

    const updateCompact =
      () => {
        setFilterCompact(
          host.scrollTop > 96
        );
      };

    updateCompact();

    host.addEventListener(
      "scroll",
      updateCompact,
      {
        passive: true,
      }
    );

    return () => {
      host.removeEventListener(
        "scroll",
        updateCompact
      );
    };
  }, [
    isExpeditor,
    view,
  ]);

  const marketFilterSummary =
    useMemo(
      () => {
        const parts = [];

        const fromRaw =
          asText(
            filters.from
          );

        const toRaw =
          asText(
            filters.to
          );

        const from =
          isExpeditor
            ? expeditorRoutePlace(
                fromRaw
              )
            : fromRaw;

        const to =
          isExpeditor
            ? expeditorRoutePlace(
                toRaw
              )
            : toRaw;

        if (
          from &&
          to
        ) {
          parts.push(
            `${from} → ${to}`
          );
        } else if (from) {
          parts.push(
            `${t.from}: ${from}`
          );
        } else if (to) {
          parts.push(
            `${t.to}: ${to}`
          );
        }

        if (
          asText(
            filters.truck
          )
        ) {
          parts.push(
            truckLabel(
              filters.truck
            )
          );
        }

        if (
          filters.dateMode ===
          "today"
        ) {
          parts.push(
            t.today
          );
        }

        if (
          filters.dateMode ===
          "tomorrow"
        ) {
          parts.push(
            t.tomorrow
          );
        }

        return parts.join(
          " · "
        );
      },
      [
        filters,
        isExpeditor,
        t,
      ]
    );

  // REISOR_STAGE158B2_DRIVER_FILTER_STATE_PARITY_V1
  const hasActiveMarketFilters =
    Boolean(
      asText(
        filters.from
      ) ||
      asText(
        filters.to
      ) ||
      asText(
        filters.truck
      ) ||
      filters.dateMode !==
        "all" ||
      marketAgeWindowHours !== 24
    );

  const truckFilterRequired =
    isExpeditor &&
    !asText(
      filters.truck
    );

  // REISOR_MARKET_EXPEDITOR_SAVED_SEARCH_UI_V1
  // Match Driver behaviour: persistent alerts save route + body only, not Today/Tomorrow.
  const savedSearchFrom = asText(filters.from);
  const savedSearchTo = asText(filters.to);
  const savedSearchTruck = asText(filters.truck).toUpperCase();
  const savedSearchKey = [
    savedSearchFrom.toLowerCase(),
    savedSearchTo.toLowerCase(),
    savedSearchTruck,
  ].join("|");
  const canSaveCurrentSearch = Boolean(
    isExpeditor && marketToken && savedSearchFrom && savedSearchTo && savedSearchTruck
  );
  const currentSavedSearchState =
    savedSearchState.key === savedSearchKey
      ? savedSearchState
      : { key: savedSearchKey, status: "idle", message: "" };

  const [draft, setDraft] =
    useState(
      () =>
        restoredNewDraft
          ?.draft ||
        emptyDraft()
    );

  const [step, setStep] =
    useState(
      () =>
        restoredNewDraft
          ?.step ||
        1
    );

  const [
    editingId,
    setEditingId,
  ] = useState("");

  const [
    formBusy,
    setFormBusy,
  ] = useState(false);

  const [
    formError,
    setFormError,
  ] = useState("");

  const [
    formSuccess,
    setFormSuccess,
  ] = useState("");

  const [
    closeTarget,
    setCloseTarget,
  ] = useState(null);
  const [
    mineState,
    setMineState,
  ] = useState(
    "ACTIVE"
  );

  const [
    mineItems,
    setMineItems,
  ] = useState([]);

  const [
    mineBusy,
    setMineBusy,
  ] = useState(false);

  const [
    mineError,
    setMineError,
  ] = useState("");

  useEffect(() => {
    if (
      view !== "add" ||
      editingId
    ) {
      return;
    }

    saveMarketDraft(
      role,
      user,
      {
        draft,
        step,
        view,
      }
    );
  }, [
    draft,
    step,
    view,
    editingId,
    role,
    user,
  ]);

  const effectiveDate =
    filters.dateMode ===
      "today"
      ? localDateIso(0)
      : filters.dateMode ===
          "tomorrow"
        ? localDateIso(1)
        : "";

  async function loadMarket({ append = false } = {}) {
    const requestSeq =
      ++marketLoadSeqRef.current;
    const pageOffset =
      append
        ? marketNextOffsetRef.current
        : 0;

    if (append) {
      setMarketMoreBusy(true);
    } else {
      setMarketBusy(true);
    }
    setMarketError("");

    try {
      const params =
        new URLSearchParams();

      params.set(
        "limit",
        String(MARKET_PAGE_SIZE)
      );
      params.set(
        "offset",
        String(pageOffset)
      );

      // REISOR_PRO_CARGO_UNFILTERED_QUERY_V2
      // CARGO always receives the full live showcase.
      // Working search parameters belong only to EXPEDITOR.
      if (isExpeditor) {
        if (
          asText(
            filters.from
          )
        ) {
          params.set(
            "from",
            asText(
              filters.from
            )
          );
        }

        if (
          asText(
            filters.to
          )
        ) {
          params.set(
            "to",
            asText(
              filters.to
            )
          );
        }

        if (
          asText(
            filters.truck
          )
        ) {
          params.set(
            "truck",
            asText(
              filters.truck
            )
          );
        }

        if (
          effectiveDate
        ) {
          params.set(
            "loadingDate",
            effectiveDate
          );
        }

        params.set(
          "maxAgeHours",
          String(marketAgeWindowHours)
        );
      }

      const data =
        await publicRequest(
          `/api/driver/freight-market/items?${params.toString()}`
        );

      if (
        requestSeq !==
        marketLoadSeqRef.current
      ) {
        return;
      }

      const rows =
        Array.isArray(
          data?.items
        )
          ? data.items
          : [];

      if (append) {
        marketNextOffsetRef.current = pageOffset + rows.length;
      } else {
        marketNextOffsetRef.current = rows.length;
      }

      // REISOR_STAGE158B1_SERVER_AUTHORITATIVE_FILTER_V1
      //
      // DRIVER and EXPEDITOR now consume the exact same
      // backend-filtered result.
      //
      // No second filtering against translated/display text.
      if (append) {
        setMarketItems((previous) => {
          const seen = new Set(
            previous.map((item) =>
              asText(item?.id)
            )
          );
          const extra = rows.filter((item) => {
            const id = asText(item?.id);
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          return [
            ...previous,
            ...extra,
          ];
        });
      } else {
        setMarketItems(
          rows
        );
      }

      const serverTotal =
        Number(
          data?.filteredTotal ??
          data?.total ??
          rows.length
        ) ||
        rows.length;

      setMarketTotal(
        serverTotal
      );

      const serverTotal24h =
        Number(
          data?.availableTotal24h ??
          data?.radarTotal24h ??
          serverTotal
        );

      setMarketTotal24h(
        Number.isFinite(serverTotal24h) &&
        serverTotal24h >= 0
          ? serverTotal24h
          : serverTotal
      );
    } catch {
      if (
        requestSeq !==
        marketLoadSeqRef.current
      ) {
        return;
      }

      setMarketError(
        t.marketUnavailable
      );
    } finally {
      if (
        requestSeq ===
        marketLoadSeqRef.current
      ) {
        if (append) {
          setMarketMoreBusy(false);
        } else {
          setMarketBusy(false);
        }
      }
    }
  }

  async function ensureMarketExpeditorNativePushToken() {
    if (!isExpeditor || !marketToken) {
      return {
        ok: false,
        reason: "auth",
      };
    }

    const nativeToken =
      readMarketExpeditorNativePushToken();

    const marketUserId =
      asText(user?.id);

    const bindingKey =
      marketUserId
        ? `reisor.market_expeditor.push.bound:${marketUserId}`
        : "";

    if (!nativeToken) {
      return {
        ok: false,
        reason: "no_native_token",
      };
    }

    if (bindingKey) {
      try {
        const alreadyBound =
          asText(
            localStorage.getItem(bindingKey)
          );

        if (alreadyBound === nativeToken) {
          return {
            ok: true,
            channel: "native",
            cached: true,
          };
        }
      } catch {}
    }

    await boardRequest(
      "/freight-market/push/register-native-token",
      {
        token: marketToken,
        method: "POST",
        body: {
          token: nativeToken,
          platform: "android",
          appVersion: "reisor-pro-market",
        },
      }
    );

    if (bindingKey) {
      try {
        localStorage.setItem(
          bindingKey,
          nativeToken
        );
      } catch {}
    }

    return {
      ok: true,
      channel: "native",
      cached: false,
    };
  }

  async function ensureMarketExpeditorPushSubscription() {
    if (!isExpeditor || !marketToken) {
      return {
        ok: false,
        reason: "auth",
      };
    }

    try {
      const native =
        await ensureMarketExpeditorNativePushToken();

      if (native?.ok) {
        return native;
      }
    } catch {}

    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window)
    ) {
      return { ok: false, reason: "unsupported" };
    }

    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return { ok: false, reason: "permission" };

    const keyData = await boardRequest("/freight-market/push/public-key", {
      token: marketToken,
    });
    const publicKey = asText(keyData?.publicKey);
    if (!publicKey) return { ok: false, reason: "key" };

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await boardRequest("/freight-market/push/subscribe", {
      token: marketToken,
      method: "POST",
      body: {
        subscription:
          typeof subscription?.toJSON === "function"
            ? subscription.toJSON()
            : subscription,
      },
    });
    return { ok: true };
  }

  function openRoleLegal(kind, title) {
    const url = marketLegalUrl(kind, role, lang);
    if (!url) return;
    setLegalDoc({ title, url });
  }

  async function enableRoleNotifications() {
    if (!isExpeditor || pushSettingsState.busy) return;
    setPushSettingsState({ busy: true, ok: false, message: "" });
    try {
      const result = await ensureMarketExpeditorPushSubscription();
      if (!result?.ok) {
        setPushSettingsState({ busy: false, ok: false, message: t.notificationsUnavailable });
        return;
      }
      setPushSettingsState({ busy: false, ok: true, message: t.notificationsEnabled });
    } catch {
      setPushSettingsState({ busy: false, ok: false, message: t.notificationsUnavailable });
    }
  }

  async function loadSavedFilters({ silent = false } = {}) {
    if (!isExpeditor || !marketToken) {
      setSavedFilters([]);
      return;
    }
    if (!silent) setSavedFiltersLoading(true);
    setSavedFiltersError("");
    try {
      const data = await boardRequest("/freight-market/saved-searches", {
        token: marketToken,
      });
      setSavedFilters(marketSavedSearchItems(data));
    } catch {
      setSavedFiltersError(t.savedFiltersLoadError);
    } finally {
      if (!silent) setSavedFiltersLoading(false);
    }
  }

  async function saveCurrentFreightSearch() {
    if (!canSaveCurrentSearch || currentSavedSearchState.status === "saving") return;
    setSavedSearchState({ key: savedSearchKey, status: "saving", message: "" });

    let pushReady = false;
    try {
      try {
        const push = await ensureMarketExpeditorPushSubscription();
        pushReady = Boolean(push?.ok);
      } catch {
        pushReady = false;
      }

      const data = await boardRequest("/freight-market/saved-searches", {
        token: marketToken,
        method: "POST",
        body: {
          from: savedSearchFrom,
          to: savedSearchTo,
          truck: savedSearchTruck,
          loadingDate: "",
        },
      });
      if (!data?.item) throw new Error("SAVE_FAILED");

      setSavedSearchState({
        key: savedSearchKey,
        status: "saved",
        message: pushReady ? "" : t.notificationPermissionNeeded,
      });
      if (savedFiltersOpen) void loadSavedFilters({ silent: true });
    } catch (error) {
      const code = asText(error?.data?.error || error?.message);
      setSavedSearchState({
        key: savedSearchKey,
        status: "error",
        message: code === "SAVED_SEARCH_LIMIT_REACHED" ? t.savedSearchLimit : t.savedSearchError,
      });
    }
  }

  async function toggleSavedFilterNotifications(item) {
    const id = asText(item?.id);
    if (!id || !marketToken || savedFilterBusyId) return;
    setSavedFilterBusyId(id);
    setSavedFiltersError("");
    try {
      const nextEnabled = !item?.notificationsEnabled;
      if (nextEnabled) {
        try { await ensureMarketExpeditorPushSubscription(); } catch {}
      }
      const data = await boardRequest(`/freight-market/saved-searches/${encodeURIComponent(id)}`, {
        token: marketToken,
        method: "PATCH",
        body: { notificationsEnabled: nextEnabled },
      });
      if (data?.item?.id) {
        setSavedFilters((current) =>
          current.map((row) => asText(row?.id) === id ? data.item : row)
        );
      } else {
        await loadSavedFilters({ silent: true });
      }
    } catch (error) {
      const code = asText(error?.data?.error || error?.message);
      setSavedFiltersError(code === "SAVED_SEARCH_LIMIT_REACHED" ? t.savedSearchLimit : t.savedFiltersUpdateError);
    } finally {
      setSavedFilterBusyId("");
    }
  }

  async function deleteSavedFilter(item) {
    const id = asText(item?.id);
    if (!id || !marketToken || savedFilterBusyId) return;
    const route = `${expeditorRoutePlace(item?.from)} → ${expeditorRoutePlace(item?.to)}`;
    if (!window.confirm(`${t.savedFiltersDelete}: ${route}?`)) return;

    setSavedFilterBusyId(id);
    setSavedFiltersError("");
    try {
      await boardRequest(`/freight-market/saved-searches/${encodeURIComponent(id)}`, {
        token: marketToken,
        method: "DELETE",
      });
      setSavedFilters((current) => current.filter((row) => asText(row?.id) !== id));
    } catch {
      setSavedFiltersError(t.savedFiltersDeleteError);
    } finally {
      setSavedFilterBusyId("");
    }
  }

  function applySavedFilter(item) {
    setFilters({
      from: asText(item?.from),
      to: asText(item?.to),
      truck: asText(item?.truck),
      dateMode: "all",
    });
    setSavedFiltersOpen(false);
    setFilterCompact(false);
  }

  async function checkAiFreightSearch(criteria = {}) {
    if (!isExpeditor || !marketToken) return { ok: true, exists: false, item: null };
    const params = new URLSearchParams({
      from: asText(criteria?.from),
      to: asText(criteria?.to),
      truck: asText(criteria?.truck).toUpperCase(),
      loadingDate: "",
    });
    return boardRequest(`/freight-market/saved-searches/check?${params.toString()}`, { token: marketToken });
  }

  async function saveAiFreightSearch(criteria = {}) {
    if (!isExpeditor || !marketToken) {
      const error = new Error("MARKET_AUTH_REQUIRED");
      error.code = "MARKET_AUTH_REQUIRED";
      throw error;
    }

    try {
      try { await ensureMarketExpeditorPushSubscription(); } catch {}

      const data = await boardRequest("/freight-market/saved-searches", {
        token: marketToken,
        method: "POST",
        body: {
          from: asText(criteria?.from),
          to: asText(criteria?.to),
          truck: asText(criteria?.truck).toUpperCase(),
          loadingDate: "",
        },
      });

      if (!data?.item) {
        const error = new Error("SAVE_FAILED");
        error.code = "SAVE_FAILED";
        throw error;
      }

      if (savedFiltersOpen) void loadSavedFilters({ silent: true });
      return data.item;
    } catch (error) {
      const code = asText(error?.data?.error || error?.code || error?.message || "SAVE_FAILED");
      error.code = code;
      throw error;
    }
  }

  function openAiFreight(freightId) {
    const id = asText(freightId);
    if (!id) return;
    setAiOpen(false);
    setView("market");
    void openMarket({ id });
  }

  function showAiSearchInMarket(searchSpec = {}) {
    const loadingDate = asText(searchSpec?.loadingDate);
    setFilters({
      from: asText(searchSpec?.from),
      to: asText(searchSpec?.to),
      truck: asText(searchSpec?.truck).toUpperCase(),
      dateMode:
        loadingDate === localDateIso(0)
          ? "today"
          : loadingDate === localDateIso(1)
            ? "tomorrow"
            : "all",
    });
    setAiOpen(false);
    setView("market");
    setFiltersOpen(false);
    setSavedFiltersOpen(false);
    setFilterCompact(true);
  }

  async function openMarket(
    item
  ) {
    const id =
      asText(
        item?.id
      );

    if (!id) {
      setMarketDetail(
        item
      );
      return;
    }

    setMarketBusy(true);

    try {
      const encodedId =
        encodeURIComponent(
          id
        );

      const data =
        isExpeditor
          ? await boardRequest(
              `/freight-market/items/${encodedId}`,
              {
                token:
                  marketToken,
              }
            )
          : await publicRequest(
              `/api/driver/freight-market/items/${encodedId}`
            );

      const detail =
        data?.item ||
        item;

      setMarketDetail({
        ...detail,
        contactAccess:
          data?.contactAccess ||
          detail?.contactAccess ||
          "",
        trialUntil:
          data?.trialUntil ||
          detail?.trialUntil ||
          null,
      });
    } catch {
      // Fail closed: never promote public/list data to contact access.
      setMarketDetail(
        item
      );
    } finally {
      setMarketBusy(false);
    }
  }

  useEffect(() => {
    if (
      view !==
      "market"
    ) {
      return undefined;
    }

    if (!isExpeditor) {
      void loadMarket();
      return undefined;
    }

    // REISOR_STAGE158B1_EXPEDITOR_AUTO_FILTER_V1
    // Driver-style automatic filter application.
    // Typing is debounced so we do not hit API on every keystroke.
    let stopped = false;

    const timer =
      window.setTimeout(
        () => {
          if (!stopped) {
            void loadMarket();
          }
        },
        260
      );

    return () => {
      stopped = true;
      window.clearTimeout(
        timer
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    isExpeditor,
    filters.from,
    filters.to,
    filters.truck,
    filters.dateMode,
    marketAgeWindowHours,
  ]);

  async function loadMine(
    state =
      mineState
  ) {
    setMineBusy(true);
    setMineError("");

    try {
      const data =
        await boardRequest(
          `/posts/mine?state=${encodeURIComponent(
            state
          )}&limit=200`,
          {
            token:
              marketToken,
          }
        );

      setMineItems(
        Array.isArray(
          data?.items
        )
          ? data.items
          : []
      );
    } catch {
      setMineError(
        t.requestFailed
      );

      setMineItems([]);
    } finally {
      setMineBusy(false);
    }
  }

  useEffect(() => {
    if (
      view ===
      "mine"
    ) {
      void loadMine(
        mineState
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    mineState,
  ]);

  function setViewSafe(
    next
  ) {
    setFormError("");
    setFormSuccess("");

    if (!editingId) {
      saveMarketDraft(
        role,
        user,
        {
          draft,
          step,
          view: next,
        }
      );
    }

    if (
      next !== "add"
    ) {
      setEditingId("");
    }

    setQuickCreateOpen(false);
    setView(next);
  }

  useEffect(() => {
    if (!isExpeditor || !marketToken) {
      return undefined;
    }

    let stopped = false;

    const syncNativePush = () => {
      if (stopped) return;

      void ensureMarketExpeditorNativePushToken()
        .catch(() => {});
    };

    syncNativePush();

    const timer =
      window.setInterval(
        syncNativePush,
        15000
      );

    window.addEventListener(
      "focus",
      syncNativePush
    );

    return () => {
      stopped = true;

      window.clearInterval(timer);

      window.removeEventListener(
        "focus",
        syncNativePush
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isExpeditor,
    marketToken,
  ]);

  useEffect(() => {
    if (!isExpeditor || !filtersOpen || !savedFiltersOpen) return;
    void loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpeditor, filtersOpen, savedFiltersOpen, marketToken]);

  useEffect(() => {
    if (!isExpeditor || !marketToken) return undefined;

    const consumeAndOpenDeepFreight = (eventFreightId = "") => {
      let freightId = asText(eventFreightId);

      try {
        if (!freightId) {
          freightId = asText(
            localStorage.getItem(MARKET_EXPEDITOR_DEEP_FREIGHT_KEY)
          );
        }

        if (!freightId) {
          freightId = asText(
            localStorage.getItem("deep_market_freight_id")
          );
        }

        if (freightId) {
          localStorage.removeItem(MARKET_EXPEDITOR_DEEP_FREIGHT_KEY);
          localStorage.removeItem("deep_market_freight_id");
        }
      } catch {}

      if (!freightId) return;

      setView("market");
      void openMarket({ id: freightId });
    };

    const onDeepFreight = (event) => {
      consumeAndOpenDeepFreight(event?.detail?.freightId);
    };

    consumeAndOpenDeepFreight();

    // Cold-start guard: App.jsx may persist the initial marketFreightId
    // after this child effect has already mounted.
    const coldStartRetry = window.setTimeout(() => {
      consumeAndOpenDeepFreight();
    }, 300);

    window.addEventListener(
      "reisor_driver_open_market_freight",
      onDeepFreight
    );

    return () => {
      window.clearTimeout(coldStartRetry);
      window.removeEventListener(
        "reisor_driver_open_market_freight",
        onDeepFreight
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpeditor, marketToken]);

  function prepareAiCargoDraft(aiDraft = {}) {
    if (isExpeditor) return;

    const allowedTrucks = new Set([
      "TENT", "REF", "ISOTHERM", "BOX", "CONTAINER", "PLATFORM",
      "MEGA", "PARAVOZ", "ISUZU", "BONGO", "SAMOSVAL", "GAZEL",
    ]);
    const truck = asText(aiDraft?.truck).toUpperCase();
    const weight = aiDraft?.weightTons == null || aiDraft?.weightTons === "" ? null : Number(aiDraft.weightTons);
    const volume = aiDraft?.volumeM3 == null || aiDraft?.volumeM3 === "" ? null : Number(aiDraft.volumeM3);
    const requiredTrucks = aiDraft?.requiredTrucks == null || aiDraft?.requiredTrucks === "" ? null : Number(aiDraft.requiredTrucks);
    const loadingDate = asText(aiDraft?.loadingDate);

    setEditingId("");
    setDraft({
      ...emptyDraft(),
      from: asText(aiDraft?.from),
      to: asText(aiDraft?.to),
      cargo: asText(aiDraft?.cargo),
      weightTons: Number.isFinite(weight) && weight > 0 ? String(weight) : "",
      volumeM3: Number.isFinite(volume) && volume >= 0 ? String(volume) : "",
      truckTypes: allowedTrucks.has(truck) ? [truck] : [],
      requiredTrucks: Number.isInteger(requiredTrucks) && requiredTrucks >= 1 && requiredTrucks <= 1000
        ? String(requiredTrucks)
        : "1",
      loadingDate: /^\d{4}-\d{2}-\d{2}$/.test(loadingDate) ? loadingDate : "",
      loadingReady: aiDraft?.loadingReady === true,
      comment: asText(aiDraft?.comment),
    });
    setStep(1);
    setFormError("");
    setFormSuccess("");
    setAiOpen(false);
    setView("add");
  }

  function beginNew() {
    const saved =
      readMarketDraft(
        role,
        user
      );

    setEditingId("");
    setDraft(
      saved?.draft ||
      emptyDraft()
    );
    setStep(
      saved?.step ||
      1
    );
    setFormError("");
    setFormSuccess("");
    setView("add");
  }

  function beginNewForDriver(driver) {
    const driverId = asText(driver?.id);
    if (!driverId) return;

    setPendingVehicleInvite(null);
    setPendingDriverInvite({
      id: driverId,
      fullName: asText(driver?.fullName) || t.driversNav,
    });
    setDriverInviteNotice(null);
    setEditingId("");
    setDraft(emptyDraft());
    setStep(1);
    setFormError("");
    setFormSuccess("");
    setView("add");
  }

  function beginNewForVehicle(vehicle) {
    const vehicleId = asText(vehicle?.id);
    const ownerRole = asText(vehicle?.ownerRole).toUpperCase();
    if (!vehicleId || !["DRIVER", "EXPEDITOR"].includes(ownerRole)) return;

    setPendingDriverInvite(null);
    setPendingVehicleInvite({
      id: vehicleId,
      ownerRole,
      label: [asText(vehicle?.from), asText(vehicle?.to)].filter(Boolean).join(" → ") || t.vehiclesNav,
      snapshot: vehicle,
    });
    setDriverInviteNotice(null);
    setEditingId("");
    setDraft(emptyDraft());
    setStep(1);
    setFormError("");
    setFormSuccess("");
    setView("add");
  }

  function beginEdit(
    post
  ) {
    setEditingId(
      asText(
        post?.id
      )
    );

    setDraft(
      postToDraft(
        post
      )
    );

    setStep(1);
    setFormError("");
    setFormSuccess("");
    setView("add");
  }

  function validateStep(
    wantedStep
  ) {
    if (
      wantedStep === 1
    ) {
      if (
        asText(
          draft.from
        ).length < 2 ||
        asText(
          draft.to
        ).length < 2
      ) {
        return t.routeRequired;
      }

      if (
        asText(
          draft.from
        ).toLocaleLowerCase() ===
        asText(
          draft.to
        ).toLocaleLowerCase()
      ) {
        return t.sameRoute;
      }
    }

    if (
      wantedStep === 2
    ) {
      if (
        asText(
          draft.cargo
        ).length < 2
      ) {
        return t.cargoRequired;
      }

      const weight =
        Number(
          draft.weightTons
        );

      if (
        !Number.isFinite(weight) ||
        weight < 0.1 ||
        weight > 1000
      ) {
        return marketRequestErrorText(
          {
            message:
              "BAD_WEIGHT",
          },
          lang,
          t
        );
      }

      const volumeText =
        asText(
          draft.volumeM3
        );

      if (volumeText) {
        const volume =
          Number(
            volumeText
          );

        if (
          !Number.isFinite(volume) ||
          volume < 0 ||
          volume > 10000
        ) {
          return marketRequestErrorText(
            {
              message:
                "BAD_VOLUME",
            },
            lang,
            t
          );
        }
      }

      if (
        !draft
          .truckTypes
          .length
      ) {
        return t.truckRequired;
      }

      const truckCount =
        Number(
          draft.requiredTrucks
        );

      if (
        !Number.isInteger(
          truckCount
        ) ||
        truckCount < 1 ||
        truckCount > 1000
      ) {
        return marketRequestErrorText(
          {
            message:
              "BAD_TRUCK_COUNT",
          },
          lang,
          t
        );
      }

      if (
        !draft.loadingReady &&
        !asText(
          draft.loadingDate
        )
      ) {
        return t.loadingRequired;
      }
    }

    if (wantedStep === 3) {
      if (
        !normalizePaymentType(
          draft.paymentType
        )
      ) {
        return t.paymentRequired;
      }

      if (
        draft.priceMode ===
          "FIXED" &&
        !(
          Number(
            draft.priceAmount
          ) >= 1 &&
          Number(
            draft.priceAmount
          ) <=
            1000000000000
        )
      ) {
        return t.priceRequired;
      }

      const advance =
        Number(
          draft.advancePercent ||
          0
        );

      if (
        !Number.isFinite(
          advance
        ) ||
        advance < 0 ||
        advance > 100
      ) {
        return marketRequestErrorText(
          {
            message:
              "BAD_ADVANCE",
          },
          lang,
          t
        );
      }
    }

    return "";
  }

  function nextStep() {
    const error =
      validateStep(
        step
      );

    if (error) {
      setFormError(
        error
      );
      return;
    }

    setFormError("");

    setStep(
      Math.min(
        3,
        step + 1
      )
    );
  }

  function toggleTruck(
    code
  ) {
    setDraft(
      (current) => {
        const active =
          current
            .truckTypes
            .includes(
              code
            );

        return {
          ...current,

          truckTypes:
            active
              ? current
                  .truckTypes
                  .filter(
                    (item) =>
                      item !==
                      code
                  )
              : [
                  ...current
                    .truckTypes,
                  code,
                ],
        };
      }
    );
  }

  async function submitDraft() {
    for (
      const wanted
      of [1, 2, 3]
    ) {
      const error =
        validateStep(
          wanted
        );

      if (error) {
        setStep(
          wanted
        );

        setFormError(
          error
        );

        return;
      }
    }

    setFormBusy(true);
    setFormError("");
    setFormSuccess("");

    const body = {
      from:
        asText(
          draft.from
        ),

      to:
        asText(
          draft.to
        ),

      fromGeo:
        draft.fromGeo,

      toGeo:
        draft.toGeo,

      cargo:
        asText(
          draft.cargo
        ),

      weightTons:
        Number(
          draft.weightTons
        ),

      volumeM3:
        asText(
          draft.volumeM3
        )
          ? Number(
              draft.volumeM3
            )
          : null,

      truckTypes:
        draft.truckTypes,

      requiredTrucks:
        Math.max(
          1,
          Number.parseInt(
            draft.requiredTrucks ||
            "1",
            10
          ) ||
          1
        ),

      loadingDate:
        asText(
          draft.loadingDate
        ) ||
        null,

      loadingReady:
        Boolean(
          draft.loadingReady
        ),

      priceMode:
        draft.priceMode,

      priceAmount:
        draft.priceMode ===
          "FIXED"
          ? Number(
              draft.priceAmount
            )
          : null,

      currency:
        draft.currency,

      advancePercent:
        Number(
          draft.advancePercent ||
          0
        ),

      paymentType:
        normalizePaymentType(
          draft.paymentType
        ),

      comment:
        asText(
          draft.comment
        ),
    };

    try {
      const targetedVehicleContext = !editingId && pendingVehicleInvite
        ? { ...pendingVehicleInvite }
        : null;

      if (editingId) {
        await boardRequest(
          `/posts/${encodeURIComponent(
            editingId
          )}`,
          {
            token:
              marketToken,

            method:
              "PATCH",

            body,
          }
        );

        setFormSuccess(
          t.updated
        );
      } else {
        const created = await boardRequest(
          "/posts",
          {
            token:
              marketToken,

            method:
              "POST",

            body,
          }
        );

        const createdPostId = asText(created?.post?.id);
        const targetedDriverId = asText(pendingDriverInvite?.id);

        if (targetedDriverId && createdPostId) {
          try {
            const { response, data } = await reisorProDriverRequest(
              `/api/vehicle-market/drivers/${encodeURIComponent(targetedDriverId)}/invite`,
              {
                token: marketToken,
                method: "POST",
                body: { postId: createdPostId },
              }
            );

            if (!response?.ok || !data?.ok) {
              throw new Error(data?.error || "DRIVER_INVITE_FAILED");
            }

            setDriverInviteNotice({ kind: "success", message: t.targetedInviteSent });
          } catch (inviteError) {
            console.warn("[REISOR targeted driver invite]", inviteError?.message || inviteError);
            setDriverInviteNotice({ kind: "warn", message: t.targetedInviteFailed });
          } finally {
            setPendingDriverInvite(null);
          }
        }

        const targetedVehicleId = asText(targetedVehicleContext?.id);
        const targetedVehicleOwnerRole = asText(targetedVehicleContext?.ownerRole).toUpperCase();

        if (targetedVehicleId && targetedVehicleOwnerRole && createdPostId) {
          try {
            const { response, data } = await reisorProDriverRequest(
              `/api/vehicle-market/items/${encodeURIComponent(targetedVehicleId)}/invite`,
              {
                token: marketToken,
                method: "POST",
                body: {
                  postId: createdPostId,
                  vehicleOwnerRole: targetedVehicleOwnerRole,
                },
              }
            );

            if (!response?.ok || !data?.ok) {
              throw new Error(data?.error || "VEHICLE_INVITE_FAILED");
            }

            setDriverInviteNotice({ kind: "success", message: t.targetedVehicleInviteSent });
          } catch (inviteError) {
            console.warn("[REISOR targeted vehicle invite]", inviteError?.message || inviteError);
            setDriverInviteNotice({ kind: "warn", message: t.targetedVehicleInviteFailed });
          } finally {
            setPendingVehicleInvite(null);
          }
        }

        setFormSuccess(
          t.published
        );
      }

      if (!editingId) {
        clearMarketDraft(
          role,
          user
        );
      }

      setEditingId("");
      setDraft(
        emptyDraft()
      );

      if (targetedVehicleContext?.snapshot) {
        setVehicleResumeTarget({
          key: `${targetedVehicleContext.id}:${Date.now()}`,
          item: targetedVehicleContext.snapshot,
        });
        setVehicleDiscoveryMode("vehicles");
        setView("vehicles");
      } else {
        setMineState(
          "ACTIVE"
        );

        setView(
          "mine"
        );

        await loadMine(
          "ACTIVE"
        );
      }
    } catch (error) {
      setFormError(
        marketRequestErrorText(
          error,
          lang,
          t
        )
      );
    } finally {
      setFormBusy(false);
    }
  }

  function closePost(
    post
  ) {
    const id =
      asText(
        post?.id
      );

    if (!id) {
      return;
    }

    setCloseTarget(post);
  }

  async function confirmClosePost() {
    const id =
      asText(
        closeTarget?.id
      );

    if (!id) {
      setCloseTarget(null);
      return;
    }

    setCloseTarget(null);
    setMineBusy(true);
    setMineError("");

    try {
      await boardRequest(
        `/posts/${encodeURIComponent(
          id
        )}/close`,
        {
          token:
            marketToken,

          method:
            "POST",
        }
      );

      await loadMine(
        mineState
      );
    } catch {
      setMineError(
        t.requestFailed
      );
    } finally {
      setMineBusy(false);
    }
  }

  useEffect(() => {
    if (!isExpeditor) {
      return undefined;
    }

    const nowMs = Date.now();

    const nextExpiry =
      marketItems
        .map(
          reisorCargoPriorityUntilMs
        )
        .filter(
          (value) =>
            value > nowMs
        )
        .sort(
          (a, b) => a - b
        )[0];

    if (!nextExpiry) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () =>
          setMarketPriorityTick(
            (value) => value + 1
          ),
        Math.max(
          25,
          nextExpiry -
            nowMs +
            50
        )
      );

    return () =>
      window.clearTimeout(
        timer
      );
  }, [
    isExpeditor,
    marketItems,
    marketPriorityTick,
  ]);

  const visibleMarket =
    useMemo(
      () => {
        if (!isExpeditor) {
          return marketItems;
        }

        const nowMs = Date.now();

        return [
          ...marketItems,
        ].sort(
          (a, b) => {
            const ap =
              isReisorCargoPriorityActive(
                a,
                nowMs
              )
                ? 1
                : 0;

            const bp =
              isReisorCargoPriorityActive(
                b,
                nowMs
              )
                ? 1
                : 0;

            if (ap !== bp) {
              return bp - ap;
            }

            if (ap && bp) {
              const at =
                new Date(
                  a?.firstSeenAt ||
                  0
                ).getTime() || 0;

              const bt =
                new Date(
                  b?.firstSeenAt ||
                  0
                ).getTime() || 0;

              return bt - at;
            }

            return 0;
          }
        );
      },
      [
        isExpeditor,
        marketItems,
        marketPriorityTick,
      ]
    );

  const canLoadMoreMarket =
    marketItems.length <
    marketTotal;

  return (
    <main
      className={`rpb-page ${
        isExpeditor
          ? "rpb-page--expeditor"
          : "rpb-page--cargo"
      }`}
    >
      <div className="rpb-glow rpb-glow--one" />
      <div className="rpb-glow rpb-glow--two" />

      <section className="rpb-shell">
        <Header
          t={t}
          roleTitle={
            effectiveRoleTitle
          }
          phone={
            user?.phone
          }
          isExpeditor={isExpeditor}
          view={view}
          onBack={
            onBack
          }
          onLogout={
            onLogout
          }
          onView={
            setViewSafe
          }
          onQuickCreate={() => setQuickCreateOpen(true)}
        />

        {view ===
        "market" ? (
          <section className="rpb-view">
            {isExpeditor ? (
              <div className="rpb-title-row">
                <div>
                  <h1>{t.expeditorLoadsTitle}</h1>
                  <p>{t.marketLeadExpeditor}</p>
                </div>

                <button
                  type="button"
                  className="rpb-add-button rpb-add-button--desktop"
                  onClick={beginNew}
                >
                  <Plus size={19} aria-hidden="true" />
                  {t.add}
                </button>
              </div>
            ) : null}

            {!isExpeditor ? (
              <div
                className="rpb-cargo-market-toolbar"
                data-reisor-cargo-owner-toolbar="m4-v2"
              >
                <button
                  type="button"
                  className="rpb-add-button rpb-cargo-market-toolbar__add"
                  onClick={beginNew}
                >
                  <Plus size={19} aria-hidden="true" />
                  {t.add}
                </button>
              </div>
            ) : null}

            {/* REISOR_PRO_EXPEDITOR_DRIVER_FILTER_UI_PARITY_V3 */}
              {isExpeditor ? (
                <>
                    {/* REISOR_RECOVERED_EXPEDITOR_SEARCH_LAUNCHER_V5 */}
<button
                    type="button"
                    onClick={() => {
                      setFiltersOpen(true);
                      setFilterCompact(false);
                    }}
                    className="rpb-expeditor-filter-launcher sticky top-2 z-[80] mb-2 flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-3 py-2.5 text-left shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/95"
                    data-reisor-driver-market-sticky-filter="v4-compact-search-launcher"
                    data-reisor-expeditor-driver-ui-parity="v3"
                  >
                    <span className="rpb-expeditor-filter-launcher__icon inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                      <Search
                        size={17}
                        aria-hidden="true"
                      />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="rpb-expeditor-filter-launcher__title block text-base font-black text-slate-900 dark:text-white">
                        {t.searchOrders}
                      </span>

                      <span className="rpb-expeditor-filter-launcher__count-copy mt-0.5 block text-sm font-black text-blue-700 dark:text-blue-300">
                        {t.availableOrdersPrefix}{" "}
                        {marketTotal24h}{" "}
                        {t.availableOrdersSuffix}
                      </span>

                      {marketFilterSummary ? (
                        <span className="rpb-expeditor-filter-launcher__subtitle mt-0.5 block truncate text-[13px] font-semibold text-slate-500 dark:text-slate-400">
                          {marketFilterSummary}
                        </span>
                      ) : null}
                    </span>

                    <span className="inline-flex shrink-0 items-center gap-2">
                      {hasActiveMarketFilters ? (
                        <span className="rounded-lg bg-blue-600 px-2 py-1 text-[13px] font-black text-white">
                          {t.filterActiveBadge}
                        </span>
                      ) : null}

                      <span
                        className="rpb-expeditor-filter-launcher__chevron text-lg leading-none text-slate-400"
                        aria-hidden="true"
                      >
                        ⌄
                      </span>
                    </span>
                  </button>
                    {filtersOpen ? (
                    <>
                      <button
                        type="button"
                        aria-label={t.back}
                        onClick={() => {
                          setFiltersOpen(false);
                          setSavedFiltersOpen(false);
                        }}
                        className="fixed inset-0 z-[10010] bg-black/60"
                        data-reisor-search-sheet-backdrop="v4"
                      />

                      <div
                        className="fixed inset-x-0 bottom-0 z-[10020] mx-auto flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-3xl border border-b-0 border-slate-700 bg-slate-900 text-white shadow-2xl"
                        data-reisor-search-sheet="v4"
                        data-reisor-expeditor-search-sheet="driver-parity-v4-saved-searches"
                      >
                        <div className="mx-auto mt-2 h-1.5 w-11 rounded-full bg-slate-600" />

                        <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-2">
                          <div className="min-w-0">
                            <div className="text-base font-black text-white">
                              {t.findCargo}
                            </div>
                            <div className="mt-0.5 truncate text-[13px] font-semibold text-slate-400">
                              {marketFilterSummary || t.findCargoHint}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFiltersOpen(false);
                              setSavedFiltersOpen(false);
                            }}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-slate-200"
                            aria-label={t.back}
                          >
                            <X size={20} aria-hidden="true" />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 border-y border-slate-700/70 bg-slate-950/45 p-1.5">
                          <button
                            type="button"
                            onClick={() => setSavedFiltersOpen(false)}
                            className={`h-10 rounded-xl px-3 text-sm font-black ${
                              !savedFiltersOpen
                                ? "bg-blue-600 text-white"
                                : "bg-transparent text-slate-400"
                            }`}
                          >
                            {t.filters}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSavedFiltersOpen(true)}
                            className={`h-10 rounded-xl px-3 text-sm font-black ${
                              savedFiltersOpen
                                ? "bg-blue-600 text-white"
                                : "bg-transparent text-slate-400"
                            }`}
                            data-reisor-expeditor-saved-filters-tab="v1"
                          >
                            {t.savedFilters}{savedFilters.length ? ` · ${savedFilters.length}` : ""}
                          </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3">
                          {!savedFiltersOpen ? (
                            <div className="space-y-3" data-reisor-search-sheet-filters="v4">
                              <div className="grid grid-cols-2 gap-2">
                                <label className="relative block">
                                  <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
                                  <input
                                    value={filters.from}
                                    enterKeyHint="search"
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        try { event.currentTarget.blur(); } catch {}
                                      }
                                    }}
                                    onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
                                    placeholder={t.from}
                                    className="h-11 w-full rounded-xl border border-slate-700 bg-slate-950 pl-9 pr-2 text-sm font-semibold text-white outline-none focus:border-blue-500"
                                  />
                                </label>

                                <label className="relative block">
                                  <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
                                  <input
                                    value={filters.to}
                                    enterKeyHint="search"
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        try { event.currentTarget.blur(); } catch {}
                                      }
                                    }}
                                    onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
                                    placeholder={t.to}
                                    className="h-11 w-full rounded-xl border border-slate-700 bg-slate-950 pl-9 pr-2 text-sm font-semibold text-white outline-none focus:border-blue-500"
                                  />
                                </label>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className={`rounded-xl px-3 py-2 text-[14px] font-bold ${filters.dateMode === "today" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
                                  onClick={() => setFilters((current) => ({ ...current, dateMode: current.dateMode === "today" ? "all" : "today" }))}
                                >
                                  {t.todayLoading}
                                </button>
                                <button
                                  type="button"
                                  className={`rounded-xl px-3 py-2 text-[14px] font-bold ${filters.dateMode === "tomorrow" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
                                  onClick={() => setFilters((current) => ({ ...current, dateMode: current.dateMode === "tomorrow" ? "all" : "tomorrow" }))}
                                >
                                  {t.tomorrowLoading}
                                </button>
                              </div>

                              <div className="grid grid-cols-2 gap-2" data-reisor-expeditor-age-window="24-default-v3">
                                {[6, 12].map((hours) => (
                                  <button
                                    key={hours}
                                    type="button"
                                    aria-pressed={marketAgeWindowHours === hours}
                                    onClick={() => setMarketAgeWindowHours((current) => current === hours ? 24 : hours)}
                                    className={`rounded-xl px-3 py-2 text-[14px] font-bold ${
                                      marketAgeWindowHours === hours
                                        ? "bg-blue-600 text-white"
                                        : "bg-slate-800 text-slate-300"
                                    }`}
                                  >
                                    {hours === 6 ? t.loads6h : t.loads12h}
                                  </button>
                                ))}
                              </div>

                              <div className="flex items-center gap-2" data-reisor-driver-market-truck-collapse="v3-picker">
                                <VehicleTypePicker
                                  value={filters.truck}
                                  onChange={(value) => setFilters((current) => ({ ...current, truck: String(value || "") }))}
                                  domain="freight"
                                  className="min-w-0 flex-1"
                                />
                                {hasActiveMarketFilters ? (
                                  <button
                                    type="button"
                                    className="h-11 rounded-xl bg-slate-800 px-3 text-[14px] font-bold text-slate-200"
                                    onClick={() => { setFilters({ from: "", to: "", truck: "", dateMode: "all" }); setMarketAgeWindowHours(24); }}
                                  >
                                    {t.reset}
                                  </button>
                                ) : null}
                              </div>

                              {truckFilterRequired ? (
                                <div className="text-[13px] font-semibold text-amber-300">
                                  {t.filterTruckRequiredHint}
                                </div>
                              ) : null}

                              <button
                                type="button"
                                onClick={() => {
                                  setFilterCompact(true);
                                  setFiltersOpen(false);
                                  setSavedFiltersOpen(false);
                                  void loadMarket();
                                }}
                                className="h-11 w-full rounded-xl bg-blue-600 px-4 text-sm font-black text-white active:bg-blue-700"
                                data-reisor-search-sheet-show="v4"
                              >
                                {t.showFreights} · {marketTotal}
                              </button>

                              {canSaveCurrentSearch ? (
                                <div className="rounded-2xl border border-blue-500/25 bg-blue-950/35 p-3" data-reisor-expeditor-saved-search-cta="v1">
                                  <div className="flex items-start gap-2.5">
                                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-600/15 text-blue-300">
                                      <Bell size={16} aria-hidden="true" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-black text-white">{t.savedSearchTitle}</div>
                                      <div className="mt-0.5 truncate text-[13px] font-bold text-blue-200">
                                        {`${expeditorRoutePlace(savedSearchFrom)} → ${expeditorRoutePlace(savedSearchTo)} · ${truckLabel(savedSearchTruck)}`}
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => void saveCurrentFreightSearch()}
                                    disabled={currentSavedSearchState.status === "saving" || currentSavedSearchState.status === "saved"}
                                    className={`mt-3 h-10 w-full rounded-xl px-4 text-sm font-black transition ${
                                      currentSavedSearchState.status === "saved"
                                        ? "bg-emerald-600/20 text-emerald-200"
                                        : "bg-blue-600 text-white active:bg-blue-700 disabled:opacity-70"
                                    }`}
                                  >
                                    {currentSavedSearchState.status === "saving"
                                      ? t.savedSearchSaving
                                      : currentSavedSearchState.status === "saved"
                                        ? t.savedSearchSaved
                                        : t.saveAndNotify}
                                  </button>

                                  {currentSavedSearchState.message ? (
                                    <div className={`mt-2 text-[13px] font-semibold ${currentSavedSearchState.status === "error" ? "text-red-300" : "text-amber-200"}`}>
                                      {currentSavedSearchState.message}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div data-reisor-expeditor-saved-filters-manager="v1">
                              {savedFiltersLoading ? (
                                <div className="px-1 py-5 text-sm font-semibold text-slate-300">{t.savedFiltersLoading}</div>
                              ) : savedFilters.length === 0 ? (
                                <div className="px-1 py-5 text-sm font-semibold text-slate-400">{t.savedFiltersEmpty}</div>
                              ) : (
                                <div className="overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-950/55">
                                  <div className="divide-y divide-slate-800">
                                    {savedFilters.map((item) => {
                                      const id = asText(item?.id);
                                      const busy = savedFilterBusyId === id;
                                      return (
                                        <div key={id} className="p-3">
                                          <div className="flex items-start gap-2">
                                            <button type="button" onClick={() => applySavedFilter(item)} className="min-w-0 flex-1 text-left">
                                              <div className="truncate text-sm font-black text-white">
                                                {`${expeditorRoutePlace(item?.from)} → ${expeditorRoutePlace(item?.to)}`}
                                              </div>
                                              <div className="mt-1 text-[13px] font-bold text-sky-200">{truckLabel(item?.truck)}</div>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => void deleteSavedFilter(item)}
                                              disabled={busy}
                                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-red-400/20 bg-red-500/10 text-red-300 disabled:opacity-50"
                                              aria-label={t.savedFiltersDelete}
                                              title={t.savedFiltersDelete}
                                            >
                                              <Trash2 size={16} aria-hidden="true" />
                                            </button>
                                          </div>
                                          <div className="mt-2 flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => applySavedFilter(item)}
                                              className="h-9 flex-1 rounded-xl bg-blue-600 px-3 text-[13px] font-black text-white active:bg-blue-700"
                                            >
                                              {t.savedFiltersApply}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => void toggleSavedFilterNotifications(item)}
                                              disabled={busy}
                                              className={`h-9 rounded-xl px-3 text-[13px] font-black disabled:opacity-50 ${
                                                item?.notificationsEnabled
                                                  ? "bg-emerald-500/15 text-emerald-200"
                                                  : "bg-slate-800 text-slate-300"
                                              }`}
                                            >
                                              {item?.notificationsEnabled ? t.savedFiltersNotificationsOn : t.savedFiltersNotificationsOff}
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {savedFiltersError ? (
                                <div className="mt-2 rounded-xl border border-red-400/15 px-3 py-2 text-[13px] font-semibold text-red-300">
                                  {savedFiltersError}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}

              <div className="rpb-market-meta">
              {!isExpeditor ? (
                <span>
                  {t.loadsFound}:{" "}
                  <strong>
                    {marketTotal24h}
                  </strong>
                </span>
              ) : null}

              <button
                type="button"
                onClick={() =>
                  void loadMarket()
                }
                disabled={
                  marketBusy
                }
              >
                <RefreshCw
                  size={16}
                  className={
                    marketBusy
                      ? "rpb-spin"
                      : ""
                  }
                  aria-hidden="true"
                />

                {t.refresh}
              </button>
            </div>

            {marketError ? (
              <div className="rpb-error">
                {marketError}
              </div>
            ) : null}

            {marketBusy &&
            !visibleMarket
              .length ? (
              <div className="rpb-empty">
                <Loader2
                  size={24}
                  className="rpb-spin"
                  aria-hidden="true"
                />

                {t.loadingBusy}
              </div>
            ) : null}

            {!marketBusy &&
            !visibleMarket
              .length ? (
              <div className="rpb-empty">
                <Boxes
                  size={31}
                  aria-hidden="true"
                />

                {t.noLoads}
              </div>
            ) : null}

            <div className="rpb-load-list">
              {visibleMarket.map((item, index) => {
                const key = asText(item?.id) || index;
                const routeFrom = isExpeditor
                  ? (
                      expeditorRoutePlace(
                        item?.from,
                        item?.routeFromCanonical || item?.fromCanonical
                      ) || "—"
                    )
                  : routePlace(item?.from, item?.fromGeo);
                const routeTo = isExpeditor
                  ? (
                      expeditorRoutePlace(
                        item?.to,
                        item?.routeToCanonical || item?.toCanonical
                      ) || "—"
                    )
                  : routePlace(item?.to, item?.toGeo);
                const truck = Array.isArray(item?.truckTypes) && item.truckTypes.length
                  ? item.truckTypes.map(truckLabel).join(" · ")
                  : "—";

                if (isExpeditor) {
                  return (
                    <article
                      key={key}
                      className="rpb-expeditor-freight-card rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm dark:border-slate-700 dark:bg-slate-900/90"
                      data-reisor-expeditor-freight-card="driver-parity-v4"
                      data-reisor-progressive-freight-card="v1"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div
                            className="rpb-expeditor-freight-card__route text-[18px] font-black leading-[1.18] tracking-[-0.015em] text-slate-950 dark:text-white sm:text-[19px]"
                            data-reisor-cargo-priority="5m-v1"
                          >
                            {isReisorCargoPriorityActive(item) ? "⭐ " : ""}{routeFrom} → {routeTo}
                          </div>
                          <div className="rpb-expeditor-freight-card__date mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[13px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            <CalendarDays size={13} aria-hidden="true" />
                            {marketLoading(item)}
                          </div>
                          <div
                            className={`rpb-expeditor-freight-card__updated reisor-freight-age mt-1.5 text-[15px] font-black ${boardFreightAgeToneClass(item)}`}
                            data-reisor-expeditor-freight-age={Math.floor(boardFreightAgeHours(item) ?? -1)}
                          >
                            {t.updatedAt}: {formatBoardDateTime(boardFreightOriginAt(item), lang)}
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <div className="text-[13px] font-black uppercase tracking-[0.08em] text-slate-400">
                            {t.price}
                          </div>
                          <div className="rpb-expeditor-freight-card__rate mt-0.5 max-w-[145px] text-[20px] font-black leading-tight text-blue-600 dark:text-blue-300">
                            {marketRate(item, t)}
                          </div>
                        </div>
                      </div>

                      <div className="rpb-expeditor-freight-card__grid mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-slate-200 pt-3 dark:border-slate-800">
                        <div className="min-w-0">
                          <div className="rpb-expeditor-freight-card__label text-[13px] font-black uppercase tracking-[0.06em] text-slate-400">{t.cargo}</div>
                          <div className="rpb-expeditor-freight-card__value mt-0.5 truncate text-sm font-bold text-slate-900 dark:text-slate-100">{marketCargo(item)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="rpb-expeditor-freight-card__label text-[13px] font-black uppercase tracking-[0.06em] text-slate-400">{t.weight}</div>
                          <div className="rpb-expeditor-freight-card__value mt-0.5 truncate text-sm font-bold text-slate-900 dark:text-slate-100">{formatWeightKg(item?.weightKg)}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="rpb-expeditor-freight-card__label text-[13px] font-black uppercase tracking-[0.06em] text-slate-400">{t.truck}</div>
                          <div className="rpb-expeditor-freight-card__value mt-0.5 truncate text-sm font-bold text-slate-900 dark:text-slate-100">{truck}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="rpb-expeditor-freight-card__label text-[13px] font-black uppercase tracking-[0.06em] text-slate-400">{t.loadingDate}</div>
                          <div className="rpb-expeditor-freight-card__value mt-0.5 truncate text-sm font-bold text-slate-900 dark:text-slate-100">{marketLoading(item)}</div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => void openMarket(item)}
                        className="rpb-expeditor-freight-card__detail mt-3 inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-blue-600 px-4 text-sm font-black text-white active:bg-blue-700"
                      >
                        {t.detail}
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                    </article>
                  );
                }

                return (
                  <button
                    type="button"
                    className="rpb-load-card"
                    key={key}
                    onClick={() => void openMarket(item)}
                    data-reisor-progressive-freight-card="v1"
                  >
                    <div className="rpb-load-card__route">
                      <strong>{routeFrom} → {routeTo}</strong>
                    </div>
                    <div className="rpb-load-card__grid">
                      <span><Package size={15} aria-hidden="true" />{marketCargo(item)}</span>
                      <span><Truck size={15} aria-hidden="true" />{truck}</span>
                      <span><Boxes size={15} aria-hidden="true" />{formatWeightKg(item?.weightKg)}</span>
                      <span><CalendarDays size={15} aria-hidden="true" />{marketLoading(item)}</span>
                    </div>
                    <div className="rpb-load-card__footer">
                      <strong>{marketRate(item, t)}</strong>
                      <span>{t.detail}<ChevronRight size={16} aria-hidden="true" /></span>
                    </div>
                  </button>
                );
              })}
            </div>

            {canLoadMoreMarket ? (
              <button
                type="button"
                className="rpb-load-more"
                onClick={() =>
                  void loadMarket({
                    append: true,
                  })
                }
                disabled={marketMoreBusy}
                data-reisor-market-load-more={isExpeditor ? "expeditor-v1" : "cargo-v1"}
              >
                {marketMoreBusy
                  ? t.loadingBusy
                  : `${t.showMore} · ${Math.min(
                      MARKET_PAGE_SIZE,
                      Math.max(
                        0,
                        marketTotal - marketItems.length
                      )
                    )}`}
              </button>
            ) : null}

            <button
              type="button"
              className="rpb-floating-add"
              onClick={
                beginNew
              }
            >
              <Plus
                size={21}
                aria-hidden="true"
              />

              {t.add}
            </button>
          </section>
        ) : null}

        {driverInviteNotice ? (
          <div
            className={
              driverInviteNotice.kind === "success"
                ? "mx-3 mb-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
                : "mx-3 mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-black text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            }
          >
            {driverInviteNotice.message}
          </div>
        ) : null}

        {view === "vehicles" ? (
          <section className="rpb-view" data-reisor-universal-vehicle-market={role?.toLowerCase?.() || "market"}>
            <ReisorVehicleMarket
              token={marketToken}

              initialFilters={
                vehicleSearchPreset?.filters || null
              }

              initialFiltersKey={
                vehicleSearchPreset?.key || ""
              }

              onSaveSearch={
                isExpeditor
                  ? async (criteria) =>
                      boardRequest(
                        "/vehicle-market/saved-searches",
                        {
                          token: marketToken,
                          method: "POST",
                          body: criteria,
                        }
                      )
                  : null
              }

              onCreateFreightForVehicle={beginNewForVehicle}
              onCreateFreightForDriver={beginNewForDriver}
              actorRole={role}
              resumeVehicle={vehicleResumeTarget?.item || null}
              resumeVehicleKey={vehicleResumeTarget?.key || ""}
            />
          </section>
        ) : null}

        {isExpeditor && view === "starHub" ? (
          <section className="rpb-view">
            <ReisorProExpeditorVehicleStarHub
              token={marketToken}

              onOpenSearch={(item) => {
                setVehicleSearchPreset({
                  key: `${asText(item?.id) || "saved"}:${Date.now()}`,
                  filters: {
                    from: asText(item?.from),
                    to: asText(item?.to),

                    // Current REISOR bodyType is reused unchanged.
                    bodyType:
                      asText(item?.bodyType),

                    partialLoad:
                      Boolean(item?.partialLoad),

                    capacityTons:
                      item?.capacityTons ?? null,

                    volumeM3:
                      item?.volumeM3 ?? null,

                    readyAt:
                      item?.readyAt || null,
                  },
                });

                setViewSafe(
                  "vehicles"
                );
              }}

              onOpenPublished={() =>
                setViewSafe(
                  "myVehicles"
                )
              }

              onOpenPark={() =>
                setViewSafe(
                  "park"
                )
              }
            />
          </section>
        ) : null}

        {isExpeditor && view === "myVehicles" ? (
          <section className="rpb-view">
            <ReisorProExpeditorMyVehicles
              token={marketToken}
              onBack={() => setViewSafe("starHub")}
            />
          </section>
        ) : null}

        {isExpeditor && view === "park" ? (
          <section className="rpb-view space-y-3">
            <button
              type="button"
              onClick={() => setViewSafe("starHub")}
              className="flex min-h-[44px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-black text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <ArrowLeft size={18} aria-hidden="true" />
              <span>{t.back}</span>
            </button>

            <ReisorProExpeditorPark
              token={marketToken}
              onPreparePublish={
                prepareParkVehiclePublication
              }
            />
          </section>
        ) : null}

        {view === "settings" ? (
          <section className="rpb-view">
            <RoleSettingsPanel
              t={t}
              role={role}
              roleTitle={effectiveRoleTitle}
              user={user}
              lang={lang}
              pushState={pushSettingsState}
              onEnableNotifications={() => void enableRoleNotifications()}
              onOpenLegal={openRoleLegal}
            />
          </section>
        ) : null}

        {view ===
        "add" ? (
          <section className="rpb-view">
            <div className="rpb-title-row">
              <div>
                <h1>
                  {editingId
                    ? t.editTitle
                    : t.addTitle}
                </h1>

                <p className="rpb-free-note">
                  {t.freePublish}
                </p>
              </div>
            </div>

            {pendingDriverInvite && !editingId ? (
              <div className="mb-3 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm font-black text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
                {t.targetedDriver}: {pendingDriverInvite.fullName}
              </div>
            ) : null}

            {pendingVehicleInvite && !editingId ? (
              <div className="mb-3 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm font-black text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
                {t.targetedVehicle}: {pendingVehicleInvite.label}
              </div>
            ) : null}

            <div className="rpb-stepper">
              {[
                {
                  n: 1,
                  label:
                    t.routeStep,
                },
                {
                  n: 2,
                  label:
                    t.cargoStep,
                },
                {
                  n: 3,
                  label:
                    t.priceStep,
                },
              ].map(
                (item) => (
                  <button
                    type="button"
                    key={
                      item.n
                    }
                    className={
                      step ===
                        item.n
                        ? "is-active"
                        : step >
                            item.n
                          ? "is-done"
                          : ""
                    }
                    onClick={() => {
                      if (
                        item.n <
                        step
                      ) {
                        setStep(
                          item.n
                        );
                        setFormError(
                          ""
                        );
                      }
                    }}
                  >
                    <span>
                      {step >
                      item.n ? (
                        <Check
                          size={15}
                          aria-hidden="true"
                        />
                      ) : (
                        item.n
                      )}
                    </span>

                    <small>
                      {
                        item.label
                      }
                    </small>
                  </button>
                )
              )}
            </div>

            <div className="rpb-form-card">
              {step === 1 ? (
                <>
                  <div className="rpb-section-heading">
                    <MapPin
                      size={20}
                      aria-hidden="true"
                    />

                    <div>
                      <strong>
                        {t.routeStep}
                      </strong>

                      <small>
                        {t.step} 1 / 3
                      </small>
                    </div>
                  </div>

                  <GeoField
                    label={
                      t.from
                    }
                    placeholder={
                      t.fromPlaceholder
                    }
                    value={
                      draft.from
                    }
                    geo={
                      draft.fromGeo
                    }
                    onChange={(
                      value,
                      geo
                    ) => {
                      setDraft(
                        (
                          current
                        ) => ({
                          ...current,
                          from:
                            value,
                          fromGeo:
                            geo,
                        })
                      );

                      setFormError(
                        ""
                      );
                    }}
                  />

                  <GeoField
                    label={
                      t.to
                    }
                    placeholder={
                      t.toPlaceholder
                    }
                    value={
                      draft.to
                    }
                    geo={
                      draft.toGeo
                    }
                    onChange={(
                      value,
                      geo
                    ) => {
                      setDraft(
                        (
                          current
                        ) => ({
                          ...current,
                          to:
                            value,
                          toGeo:
                            geo,
                        })
                      );

                      setFormError(
                        ""
                      );
                    }}
                  />
                </>
              ) : null}

              {step === 2 ? (
                <>
                  <div className="rpb-section-heading">
                    <Package
                      size={20}
                      aria-hidden="true"
                    />

                    <div>
                      <strong>
                        {t.cargoStep}
                      </strong>

                      <small>
                        {t.step} 2 / 3
                      </small>
                    </div>
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.cargoName}
                    </label>

                    <input
                      value={
                        draft.cargo
                      }
                      onChange={(
                        event
                      ) => {
                        setDraft(
                          (
                            current
                          ) => ({
                            ...current,
                            cargo:
                              event
                                .target
                                .value,
                          })
                        );

                        setFormError(
                          ""
                        );
                      }}
                      placeholder={
                        t.cargoPlaceholder
                      }
                    />
                  </div>

                  <div className="rpb-two-cols">
                    <div className="rpb-field">
                      <label>
                        {t.weightTons}
                      </label>

                      <input
                        type="number"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        value={
                          draft.weightTons
                        }
                        onChange={(
                          event
                        ) =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              weightTons:
                                event
                                  .target
                                  .value,
                            })
                          )
                        }
                      />
                    </div>

                    <div className="rpb-field">
                      <label>
                        {t.volumeM3}
                      </label>

                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        value={
                          draft.volumeM3
                        }
                        onChange={(
                          event
                        ) =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              volumeM3:
                                event
                                  .target
                                  .value,
                            })
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.truckType}
                    </label>

                    <VehicleTypePicker
                      value={draft.truckTypes}
                      onChange={(truckTypes) => {
                        setDraft((current) => ({ ...current, truckTypes }));
                        setFormError("");
                      }}
                      domain="freight"
                      multiple
                    />
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.truckCount}
                    </label>

                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="1000"
                      value={
                        draft.requiredTrucks
                      }
                      onChange={(
                        event
                      ) =>
                        setDraft(
                          (
                            current
                          ) => ({
                            ...current,
                            requiredTrucks:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                    />
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.loading}
                    </label>

                    <div className="rpb-segment">
                      <button
                        type="button"
                        className={
                          !draft.loadingReady
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              loadingReady:
                                false,
                            })
                          )
                        }
                      >
                        <CalendarDays
                          size={16}
                          aria-hidden="true"
                        />

                        {t.exactDate}
                      </button>

                      <button
                        type="button"
                        className={
                          draft.loadingReady
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              loadingReady:
                                true,
                              loadingDate:
                                "",
                            })
                          )
                        }
                      >
                        <Check
                          size={16}
                          aria-hidden="true"
                        />

                        {t.readyNow}
                      </button>
                    </div>

                    {!draft
                      .loadingReady ? (
                      <input
                        type="date"
                        value={
                          draft.loadingDate
                        }
                        onChange={(
                          event
                        ) =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              loadingDate:
                                event
                                  .target
                                  .value,
                            })
                          )
                        }
                      />
                    ) : null}
                  </div>
                </>
              ) : null}

              {step === 3 ? (
                <>
                  <div className="rpb-section-heading">
                    <CircleDollarSign
                      size={20}
                      aria-hidden="true"
                    />

                    <div>
                      <strong>
                        {t.priceStep}
                      </strong>

                      <small>
                        {t.step} 3 / 3
                      </small>
                    </div>
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.priceMode}
                    </label>

                    <div className="rpb-segment">
                      <button
                        type="button"
                        className={
                          draft.priceMode ===
                            "NEGOTIABLE"
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              priceMode:
                                "NEGOTIABLE",
                              priceAmount:
                                "",
                            })
                          )
                        }
                      >
                        {t.negotiable}
                      </button>

                      <button
                        type="button"
                        className={
                          draft.priceMode ===
                            "FIXED"
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              priceMode:
                                "FIXED",
                            })
                          )
                        }
                      >
                        {t.fixed}
                      </button>
                    </div>
                  </div>

                  {draft.priceMode ===
                  "FIXED" ? (
                    <div className="rpb-two-cols">
                      <div className="rpb-field">
                        <label>
                          {t.price}
                        </label>

                        <input
                          type="number"
                          inputMode="decimal"
                          min="1"
                          value={
                            draft.priceAmount
                          }
                          onChange={(
                            event
                          ) =>
                            setDraft(
                              (
                                current
                              ) => ({
                                ...current,
                                priceAmount:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                        />
                      </div>

                      <div className="rpb-field">
                        <label>
                          {t.currency}
                        </label>

                        <div
                          className="rpb-currency-grid"
                          role="group"
                          aria-label={
                            t.currency
                          }
                        >
                          {[
                            ["UZS", "soʻm"],
                            ["USD", "$"],
                            ["RUB", "₽"],
                            ["KZT", "₸"],
                            ["EUR", "€"],
                          ].map(
                            ([
                              currency,
                              symbol,
                            ]) => (
                              <button
                                type="button"
                                key={
                                  currency
                                }
                                className={
                                  draft.currency ===
                                  currency
                                    ? "is-active"
                                    : ""
                                }
                                aria-pressed={
                                  draft.currency ===
                                  currency
                                }
                                onClick={() =>
                                  setDraft(
                                    (
                                      current
                                    ) => ({
                                      ...current,
                                      currency,
                                    })
                                  )
                                }
                              >
                                <strong>
                                  {
                                    currency
                                  }
                                </strong>
                                <span>
                                  {
                                    symbol
                                  }
                                </span>
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="rpb-two-cols">
                    <div className="rpb-field">
                      <label>
                        {t.advance}
                      </label>

                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="100"
                        value={
                          draft.advancePercent
                        }
                        onChange={(
                          event
                        ) =>
                          setDraft(
                            (
                              current
                            ) => ({
                              ...current,
                              advancePercent:
                                  event.target.value.replace(/^0+(?=\d)/, ""),
                            })
                          )
                        }
                      />
                    </div>

                    <div className="rpb-field">
                      <label>
                        {t.payment}
                      </label>

                      <div
                        className="rpb-payment-methods"
                        role="radiogroup"
                        aria-label={
                          t.paymentChoose
                        }
                      >
                        {[
                          {
                            value: "CASH",
                            label: t.cash,
                          },
                          {
                            value: "CARD",
                            label: t.card,
                          },
                          {
                            value:
                              "BANK_TRANSFER",
                            label: t.bank,
                          },
                        ].map(
                          (item) => {
                            const active =
                              draft
                                .paymentType ===
                              item.value;

                            return (
                              <button
                                key={
                                  item.value
                                }
                                type="button"
                                role="radio"
                                aria-checked={
                                  active
                                }
                                className={
                                  active
                                    ? "rpb-payment-method is-active"
                                    : "rpb-payment-method"
                                }
                                onClick={() =>
                                  setDraft(
                                    (
                                      current
                                    ) => ({
                                      ...current,
                                      paymentType:
                                        item.value,
                                    })
                                  )
                                }
                              >
                                <span>
                                  {
                                    item.label
                                  }
                                </span>

                                <strong
                                  aria-hidden="true"
                                >
                                  {
                                    active
                                      ? "✓"
                                      : ""
                                  }
                                </strong>
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rpb-field">
                    <label>
                      {t.comment}
                    </label>

                    <textarea
                      rows="4"
                      maxLength="1200"
                      value={
                        draft.comment
                      }
                      onChange={(
                        event
                      ) =>
                        setDraft(
                          (
                            current
                          ) => ({
                            ...current,
                            comment:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      placeholder={
                        t.commentPlaceholder
                      }
                    />
                  </div>

                  <div className="rpb-account-contact">
                    <LockKeyhole
                      size={17}
                      aria-hidden="true"
                    />

                    <div>
                      <strong>
                        {t.phoneFromAccount}
                      </strong>

                      <span>
                        {user?.phone ||
                          "—"}
                      </span>

                      <small>
                        {t.phoneAuto}
                      </small>
                    </div>
                  </div>
                </>
              ) : null}

              {formError ? (
                <div className="rpb-error">
                  {formError}
                </div>
              ) : null}

              {formSuccess ? (
                <div className="rpb-success">
                  {formSuccess}
                </div>
              ) : null}

              <div className="rpb-form-actions">
                {step > 1 ? (
                  <button
                    type="button"
                    className="rpb-secondary"
                    onClick={() => {
                      setStep(
                        step - 1
                      );

                      setFormError(
                        ""
                      );
                    }}
                    disabled={
                      formBusy
                    }
                  >
                    <ChevronLeft
                      size={18}
                      aria-hidden="true"
                    />

                    {t.previous}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rpb-secondary"
                    onClick={() =>
                      setViewSafe(
                        "market"
                      )
                    }
                    disabled={
                      formBusy
                    }
                  >
                    <X
                      size={18}
                      aria-hidden="true"
                    />

                    {t.back}
                  </button>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    className="rpb-primary"
                    onClick={
                      nextStep
                    }
                  >
                    {t.next}

                    <ChevronRight
                      size={18}
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rpb-primary"
                    onClick={() =>
                      void submitDraft()
                    }
                    disabled={
                      formBusy
                    }
                  >
                    {formBusy ? (
                      <Loader2
                        size={18}
                        className="rpb-spin"
                        aria-hidden="true"
                      />
                    ) : editingId ? (
                      <Check
                        size={18}
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        size={18}
                        aria-hidden="true"
                      />
                    )}

                    {formBusy
                      ? t.publishing
                      : editingId
                        ? t.save
                        : t.publish}
                  </button>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {view ===
        "mine" ? (
          <section className="rpb-view">
            <div
              className="rpb-cargo-mine-toolbar"
              data-reisor-cargo-owner-mine-toolbar="m4-v2"
            >
              <div className="rpb-mine-tabs">
                <button
                  type="button"
                  className={
                    mineState ===
                      "ACTIVE"
                      ? "is-active"
                      : ""
                  }
                  onClick={() =>
                    setMineState(
                      "ACTIVE"
                    )
                  }
                >
                  <ClipboardList
                    size={17}
                    aria-hidden="true"
                  />

                  {t.active}
                </button>

                <button
                  type="button"
                  className={
                    mineState ===
                      "HISTORY"
                      ? "is-active"
                      : ""
                  }
                  onClick={() =>
                    setMineState(
                      "HISTORY"
                    )
                  }
                >
                  <History
                    size={17}
                    aria-hidden="true"
                  />

                  {t.history}
                </button>
              </div>

              <button
                type="button"
                className="rpb-add-button rpb-cargo-mine-toolbar__add"
                onClick={beginNew}
              >
                <Plus size={18} aria-hidden="true" />
                {t.add}
              </button>
            </div>

            {mineError ? (
              <div className="rpb-error">
                {mineError}
              </div>
            ) : null}

            {mineBusy ? (
              <div className="rpb-empty">
                <Loader2
                  size={24}
                  className="rpb-spin"
                  aria-hidden="true"
                />

                {t.loadingBusy}
              </div>
            ) : null}

            {!mineBusy &&
            !mineItems.length ? (
              <div className="rpb-empty">
                <ClipboardList
                  size={30}
                  aria-hidden="true"
                />

                {mineState ===
                  "ACTIVE"
                  ? t.noMine
                  : t.noHistory}
              </div>
            ) : null}

            <div className="rpb-mine-list">
              {mineItems.map(
                (
                  post,
                  index
                ) => (
                  <article
                    className="rpb-mine-card"
                    key={
                      post?.id ||
                      index
                    }
                  >
                    <div className="rpb-mine-card__top">
                      <div className="rpb-route-stack rpb-route-stack--mine">
                        <div className="rpb-route-row rpb-route-row--from">
                          <span>
                            {t.from}
                          </span>

                          <strong>
                            {routePlace(
                              post.from,
                              post.fromGeo
                            )}
                          </strong>
                        </div>

                        <div
                          className="rpb-route-line"
                          aria-hidden="true"
                        />

                        <div className="rpb-route-row rpb-route-row--to">
                          <span>
                            {t.to}
                          </span>

                          <strong>
                            {routePlace(
                              post.to,
                              post.toGeo
                            )}
                          </strong>
                        </div>
                      </div>

                      <span
                        className={
                          post.status ===
                            "ACTIVE"
                            ? "rpb-status rpb-status--active"
                            : "rpb-status"
                        }
                      >
                        {post.status ===
                          "ACTIVE"
                          ? t.activeStatus
                          : t.closed}
                      </span>
                    </div>

                    <div className="rpb-mine-card__body">
                      <span>
                        <Package
                          size={15}
                          aria-hidden="true"
                        />

                        {post.cargo}
                      </span>

                      <span>
                        <Boxes
                          size={15}
                          aria-hidden="true"
                        />

                        {post.weightTons} t
                      </span>

                      <span>
                        <Truck
                          size={15}
                          aria-hidden="true"
                        />

                        {Array.isArray(
                          post.truckTypes
                        )
                          ? post
                              .truckTypes
                              .map(
                                truckLabel
                              )
                              .join(
                                " · "
                              )
                          : "—"}
                      </span>

                      <span>
                        <CircleDollarSign
                          size={15}
                          aria-hidden="true"
                        />

                        {post.priceMode ===
                          "FIXED"
                          ? `${formatNumber(
                              post.priceAmount
                            )} ${post.currency}`
                          : t.negotiable}
                      </span>
                    </div>

                    {post.status ===
                    "ACTIVE" ? (
                      <div className="rpb-mine-card__actions">
                        <button
                          type="button"
                          onClick={() =>
                            beginEdit(
                              post
                            )
                          }
                        >
                          <Edit3
                            size={16}
                            aria-hidden="true"
                          />

                          {t.edit}
                        </button>

                        <button
                          type="button"
                          className="is-close"
                          onClick={() =>
                            void closePost(
                              post
                            )
                          }
                        >
                          <Check
                            size={16}
                            aria-hidden="true"
                          />

                          {t.close}
                        </button>
                      </div>
                    ) : null}
                  </article>
                )
              )}
            </div>
          </section>
        ) : null}
      </section>

      {closeTarget ? (
        <div
          className="rpb-modal-backdrop"
          onMouseDown={() =>
            setCloseTarget(null)
          }
        >
          <section
            className="rpb-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t.closeConfirm}
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            <button
              type="button"
              className="rpb-modal__close"
              onClick={() =>
                setCloseTarget(null)
              }
              aria-label={t.closeCancel}
            >
              <X size={20} aria-hidden="true" />
            </button>

            <div className="rpb-modal__route">
              <Check size={20} aria-hidden="true" />
              <strong>{t.closeConfirm}</strong>
            </div>

            <div className="rpb-form-actions">
              <button
                type="button"
                className="rpb-secondary"
                onClick={() =>
                  setCloseTarget(null)
                }
              >
                {t.closeCancel}
              </button>
              <button
                type="button"
                className="rpb-primary"
                onClick={() =>
                  void confirmClosePost()
                }
              >
                <Check size={18} aria-hidden="true" />
                {t.close}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {marketDetail ? (
        <div
          className="rpb-modal-backdrop"
          onMouseDown={() =>
            setMarketDetail(
              null
            )
          }
        >
          <section
            className="rpb-modal"
            onMouseDown={(
              event
            ) =>
              event
                .stopPropagation()
            }
          >
            <button
              type="button"
              className="rpb-modal__close"
              onClick={() =>
                setMarketDetail(
                  null
                )
              }
            >
              <X
                size={20}
                aria-hidden="true"
              />
            </button>

            <div className="rpb-route-stack rpb-route-stack--modal">
              <div className="rpb-route-row rpb-route-row--from">
                <span>
                  {t.from}
                </span>

                <strong>
                  {isExpeditor
                    ? (
                        expeditorRoutePlace(
                          marketDetail?.from,
                          marketDetail?.routeFromCanonical ||
                            marketDetail?.fromCanonical
                        ) || "—"
                      )
                    : routePlace(
                        marketDetail?.from,
                        marketDetail?.fromGeo
                      )}
                </strong>
              </div>

              <div
                className="rpb-route-line"
                aria-hidden="true"
              />

              <div className="rpb-route-row rpb-route-row--to">
                <span>
                  {t.to}
                </span>

                <strong>
                  {isExpeditor
                    ? (
                        expeditorRoutePlace(
                          marketDetail?.to,
                          marketDetail?.routeToCanonical ||
                            marketDetail?.toCanonical
                        ) || "—"
                      )
                    : routePlace(
                        marketDetail?.to,
                        marketDetail?.toGeo
                      )}
                </strong>
              </div>
            </div>

            <div className="rpb-modal__grid">
              <div>
                <small>
                  {t.cargo}
                </small>

                <strong>
                  {marketCargo(
                    marketDetail
                  )}
                </strong>
              </div>

              <div>
                <small>
                  {t.weight}
                </small>

                <strong>
                  {formatWeightKg(
                    marketDetail
                      ?.weightKg
                  )}
                </strong>
              </div>

              <div>
                <small>
                  {t.truck}
                </small>

                <strong>
                  {Array.isArray(
                    marketDetail
                      ?.truckTypes
                  )
                    ? marketDetail
                        .truckTypes
                        .map(
                          truckLabel
                        )
                        .join(
                          " · "
                        )
                    : "—"}
                </strong>
              </div>

              <div>
                <small>
                  {t.price}
                </small>

                <strong>
                  {marketRate(
                    marketDetail,
                    t
                  )}
                </strong>
              </div>

              <div>
                <small>
                  {t.loadingDate}
                </small>

                <strong>
                  {marketLoading(
                    marketDetail
                  )}
                </strong>
              </div>
            </div>

            {
              isExpeditor &&
              (
                asText(marketDetail?.contactPhone) ||
                asText(marketDetail?.telegramUsername)
              )
                ? (
                  <div className="rpb-contact-lock">
                    <Check
                      size={23}
                      aria-hidden="true"
                    />

                    <div>
                      {asText(marketDetail.contactPhone) ? (
                        <strong>
                          {asText(marketDetail.contactPhone)}
                        </strong>
                      ) : null}

                      <DriverContactActions
                        phone={marketDetail.contactPhone}
                        telegramSenderId={marketDetail.telegramSenderId}
                        telegramUsername={marketDetail.telegramUsername}
                        allowPhoneTelegram={Boolean(asText(marketDetail.contactPhone))}
                        draftText={boardFreightContactDraft(marketDetail, lang)}
                        variant="hub"
                        className="rpb-expeditor-contact-actions"
                      />
                    </div>
                  </div>
                )
                : (
                  <div className="rpb-contact-lock">
                    <LockKeyhole
                      size={23}
                      aria-hidden="true"
                    />

                    <div>
                      <strong>
                        {
                          isExpeditor ? t.contactsLockedExpeditor : t.contactsLockedCargo
                        }
                      </strong>

                      <p>
                        {
                          isExpeditor ? t.contactsLockedTextExpeditor : t.contactsLockedTextCargo
                        }
                      </p>

                      <span>
                        {
                          isExpeditor ? t.contactsSoonExpeditor : t.contactsSoonCargo
                        }
                      </span>
                    </div>
                  </div>
                )
            }
          </section>
        </div>
      ) : null}

      {isExpeditor && quickCreateOpen ? (
        <div
          className="rpb-create-chooser"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setQuickCreateOpen(false);
            }
          }}
        >
          <div
            className="rpb-create-chooser__sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t.quickCreate}
          >
            <div className="rpb-create-chooser__head">
              <strong>{t.quickCreate}</strong>

              <button
                type="button"
                className="rpb-create-chooser__close"
                onClick={() => setQuickCreateOpen(false)}
                aria-label={t.closeCancel}
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="rpb-create-chooser__actions">
              <button
                type="button"
                className="rpb-create-chooser__action"
                onClick={() => {
                  setQuickCreateOpen(false);
                  beginNew();
                }}
              >
                <span className="rpb-create-chooser__icon">
                  <Package size={22} aria-hidden="true" />
                </span>

                <span>{t.createFreight}</span>
                <ChevronRight size={19} aria-hidden="true" />
              </button>

              <button
                type="button"
                className="rpb-create-chooser__action"
                onClick={() => {
                  setQuickCreateOpen(false);
                  setViewSafe("myVehicles");
                }}
              >
                <span className="rpb-create-chooser__icon">
                  <Truck size={22} aria-hidden="true" />
                </span>

                <span>{t.createVehicle}</span>
                <ChevronRight size={19} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {roleAiEntryOpen ? (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/55 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-[430px] rounded-3xl border border-slate-700 bg-slate-950 p-5 text-white shadow-2xl">
            <button type="button" onClick={closeRoleAiEntry} className="float-right flex h-9 w-9 items-center justify-center rounded-xl bg-slate-800 text-slate-300" aria-label={t.back}>
              <X size={18} />
            </button>
            <div className="pr-10 text-xl font-black">{isExpeditor ? t.aiEntryTitle : t.aiEntryCargoTitle}</div>
            {isExpeditor ? <div className="mt-2 pr-10 text-sm font-semibold leading-relaxed text-slate-400">{t.aiEntrySubtitle}</div> : null}
            <div className="mt-5 grid gap-2">
              {isExpeditor ? (
                <>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setAiEntryIntent("freight"); setAiOpen(true); }} className="min-h-[48px] rounded-2xl bg-blue-600 px-4 text-sm font-black text-white">{t.aiEntryHot}</button>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setViewSafe("vehicles"); }} className="min-h-[48px] rounded-2xl bg-slate-800 px-4 text-sm font-black text-white">{t.aiEntryFindVehicle}</button>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setViewSafe("mine"); }} className="min-h-[48px] rounded-2xl bg-slate-800 px-4 text-sm font-black text-white">{t.aiEntryDeals}</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setAiOpen(true); }} className="min-h-[48px] rounded-2xl bg-blue-600 px-4 text-sm font-black text-white">{t.aiEntryChat}</button>
                  <button type="button" onClick={() => { closeRoleAiEntry(); beginNew(); }} className="min-h-[48px] rounded-2xl bg-slate-800 px-4 text-sm font-black text-white">{t.aiEntryPublishCargo}</button>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setViewSafe("vehicles"); }} className="min-h-[48px] rounded-2xl bg-slate-800 px-4 text-sm font-black text-white">{t.aiEntryFindCarrier}</button>
                  <button type="button" onClick={() => { closeRoleAiEntry(); setViewSafe("mine"); }} className="min-h-[48px] rounded-2xl bg-slate-800 px-4 text-sm font-black text-white">{t.aiEntryMyCargo}</button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {!isExpeditor ? (
        <CargoBottomNav t={t} view={view} onView={setViewSafe} />
      ) : null}

      {legalDoc ? (
        <LegalDocsModal title={legalDoc.title} url={legalDoc.url} onClose={() => setLegalDoc(null)} />
      ) : null}

      <DriverAiAssistant
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        driverToken={marketToken}
        driverId={`market-${isExpeditor ? "expeditor" : "cargo"}:${asText(user?.id || user?._id || "anon")}`}
        onOpenFreight={isExpeditor ? openAiFreight : undefined}
        onShowInMarket={isExpeditor ? showAiSearchInMarket : undefined}
        onSaveSearch={isExpeditor ? saveAiFreightSearch : undefined}
        onCheckSavedSearch={isExpeditor ? checkAiFreightSearch : undefined}
        onPrepareVehicle={isExpeditor ? prepareAiExpeditorVehicleDraft : undefined}
        onPrepareFreight={!isExpeditor ? prepareAiCargoDraft : undefined}
        onOpenCreateFreight={!isExpeditor ? () => { setAiOpen(false); beginNew(); } : undefined}
        sendMessageRequest={sendReisorProMarketAiMessage}
        getResultSetRequest={getReisorProMarketAiResultSet}
        getQuotaRequest={getReisorProMarketAiQuota}
        surface={isExpeditor ? "expeditor" : "cargo"}
        entryIntent={isExpeditor ? aiEntryIntent : ""}
        onOpenVehicleMarket={(nextFilters = {}) => {
          setVehicleSearchPreset({
            key: `ai:${Date.now()}`,
            filters: {
              from: asText(nextFilters?.from),
              to: asText(nextFilters?.to),
              bodyType: asText(
                nextFilters?.bodyType ||
                nextFilters?.truck
              ),
              capacityTons:
                nextFilters?.capacityTons ?? null,
              volumeM3:
                nextFilters?.volumeM3 ?? null,
              readyAt:
                asText(nextFilters?.readyAt),
              partialLoad:
                Boolean(nextFilters?.partialLoad),
            },
          });
          setAiOpen(false);
          setViewSafe("vehicles");
        }}
        onOpenMyFreights={() => { setAiOpen(false); setViewSafe("mine"); }}
      />

      {isExpeditor ? (
        <ReisorProExpeditorVehicleModal
          open={Boolean(aiVehicleDraft)}

          onClose={() =>
            setAiVehicleDraft(null)
          }

          token={marketToken}

          vehicle={
            aiVehicleDraft
          }

          onCreated={() => {
            setAiVehicleDraft(null);
            setViewSafe("myVehicles");
          }}
        />
      ) : null}

      {isExpeditor && !roleAiEntryOpen && view === "market" && !marketDetail && !aiOpen && !filtersOpen ? (
        <button
          ref={aiFloatingPill.elementRef}
          type="button"
          onClick={() => { setAiEntryIntent("freight"); setAiOpen(true); }}
          {...aiFloatingPill.pointerHandlers}
          className="reisor-ai-driver-launch-pill fixed inline-flex min-h-[48px] items-center gap-2 rounded-full border pr-4 text-sm font-black"
          style={{
            right: "max(12px, env(safe-area-inset-right, 0px))",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 22px)",
            zIndex: 10020,
            ...aiFloatingPill.floatingStyle,
          }}
          aria-label="REISOR AI"
          aria-grabbed={aiFloatingPill.dragging ? "true" : "false"}
          data-reisor-ai-expeditor-pill="v03d4"
          data-reisor-ai-draggable="true"
        >
          <span className="reisor-ai-driver-avatar-wrap" aria-hidden="true">
            <img src="/icons/reisor_ai_avatar_v03d4.png" alt="" className="reisor-ai-driver-avatar" />
          </span>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          <span>REISOR AI</span>
          <ChevronRight className="h-4 w-4 opacity-80" aria-hidden="true" />
        </button>
      ) : null}

      {!isExpeditor && !roleAiEntryOpen && view === "market" && !marketDetail && !filtersOpen ? (
        <button
          ref={aiFloatingPill.elementRef}
          type="button"
          onClick={() => setAiOpen(true)}
          {...aiFloatingPill.pointerHandlers}
          className="reisor-ai-driver-launch-pill fixed inline-flex min-h-[48px] items-center gap-2 rounded-full border pr-4 text-sm font-black"
          style={{
            right: "max(12px, env(safe-area-inset-right, 0px))",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 82px)",
            zIndex: 10020,
            ...aiFloatingPill.floatingStyle,
          }}
          aria-label="REISOR AI"
          aria-grabbed={aiFloatingPill.dragging ? "true" : "false"}
          data-reisor-ai-cargo-owner-pill="m5c4-1"
          data-reisor-ai-draggable="true"
        >
          <span className="reisor-ai-driver-avatar-wrap" aria-hidden="true">
            <img src="/icons/reisor_ai_avatar_v03d4.png" alt="" className="reisor-ai-driver-avatar" />
          </span>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          <span>REISOR AI</span>
          <ChevronRight className="h-4 w-4 opacity-80" aria-hidden="true" />
        </button>
      ) : null}
    </main>
  );
}
